import { CheckCircleFilled, CloseOutlined, ExclamationCircleFilled } from "@ant-design/icons";
import { useMsal } from "@azure/msal-react";
import {
  WebDashboardContentSpec,
  WebDashboardContentSpecBlocksInner,
  DashboardLayout,
  DataTableUserOverrides,
} from "@coeff/agent-types";
import {
  AgentType,
  AnalyticsEvent,
  AnalyticsEventNameType,
  FeatureFlag,
  GetWebDashboard200Response,
  UpdateWebDashboardRequest,
  WebDashboardBuildingPhase,
  VibeDashViewerType,
  WebDashboardUpdateType,
  WebDashboardContentConfig,
  WebDashboardTabsConfig,
  WebDashboardTrialStateType,
} from "@coeff/api";
import { isWebDashboardTrialExpired } from "@coeff/utils";
import * as Sentry from "@sentry/react";
import { useFeatureGate } from "@statsig/react-bindings";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Modal } from "antd";
import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { QueryCacheKey } from "../../api/queryClient";
import { analyticsTrackAnonymous, getAnonymousUserId } from "../../api/track";
import { useWebDashboardOffer } from "../../api/users";
import { useRestoreWebDashboardCustomTheme } from "../../api/webDashboards";
import {
  EventAttributionIframe,
  GoogleAuthModal,
  GoogleOAuthErrorReason,
  MicrosoftAuthModal,
  PencilIcon,
} from "../../components";
import { config } from "../../config";
import { AppState } from "../../store";
import { isAxiosError, useApiContext, useTrack } from "../../utils";
import { isPaidPlanType } from "../Billing/enterpriseBlocker.utils";
import {
  WEB_DASHBOARD_UPGRADE_MODAL_SOURCES,
  getAddAiDashboardsUrl,
  getAiDashboardsGateProps,
  getPlanChooserUrl,
  getWebDashboardUpgradeDestination,
} from "../Billing/planChooserSource.utils";

import { resolveBlockTitle } from "./BlockAutocompleteDropdown";
import type { BlockEditUpdates } from "./BlockEditPopover";
import { ChatSidebarFlexSpacer } from "./ChatSidebarFlexSpacer";
import { ChatUnavailableModal } from "./ChatUnavailableModal";
import { DashboardBuildCompletionConfetti } from "./DashboardBuildCompletionConfetti";
import {
  BUILD_LOADER_PAGE_BACKGROUND,
  DashboardBuildContentLoader,
  DashboardBuildLoaderStyles,
  DashboardBuildPhaseProgress,
} from "./DashboardBuildLoader";
import { DashboardHeaderEdits, DashboardHeaderEditPopover } from "./DashboardHeaderEditPopover";
import { DashboardWindow, restoreTableSessionOverrides } from "./dashboardHelpers";
import { buildEditDelta } from "./editDelta";
import {
  removeBlockFromLayout,
  reorderBlocksToLayout,
  resolveEffectiveLayout,
} from "./layoutHelpers";
import { getRefreshPhaseLabel, MANUAL_REFRESH_TIMEOUT_MS } from "./refreshProgress";
import { FilterDefaultsSnapshot, withSavedDefaults } from "./slicerDefaults";
import {
  applyTailwindTheme,
  applyTheme,
  BUILTIN_PALETTES,
  DEFAULT_THEME_BACKGROUND,
  DEFAULT_THEME_ID,
  LEGACY_THEME_TO_PALETTE,
  DEFAULT_THEME_MODE,
  isCustomThemeId,
  registerCustomThemeSpec,
  resolveTheme,
  themes,
  ThemeBackground,
  ThemeMode,
  ThemeProvider,
  ThemeSpec,
} from "./themes";
import {
  FLATPICKR_STYLE_TAG,
  METRIC_STATUS_STYLE_TAG,
  TAILWIND_STYLE_TAG,
} from "./themes/applyTheme";
import { Toast, ToastProps } from "./Toast";
import {
  applyBlockPatches,
  buildBlockSpecPatch,
  useBlockEditSession,
  useBlockPatchQueue,
} from "./useBlockPatchQueue";
import { ErrorStateDetails } from "./useBlockRender";
import { NeedsGoogleOAuthError } from "./useChatMessages";
import { useChatThemeControls } from "./useChatThemeControls";
import { useCloseBlockPopoversOnOutsideTap } from "./useCloseBlockPopoversOnOutsideTap";
import { useIsMobile } from "./useIsMobile";
import { useRunSQL } from "./useRunSQL";
import { useSuperAdminDashboardVersions } from "./useSuperAdminDashboardVersions";
import { useSwipeDownToClose } from "./useSwipeDownToClose";
import { useViewTransitionStateUpdate } from "./useViewTransitionStateUpdate";
import {
  areAiImagesStaleForDashboard,
  CREATE_MODE_DASHBOARD_POLL_TIMEOUT_MS,
  isConcurrentEditRefreshAbort,
  isSaveConflictError,
  useDashboard,
  useDashboardPermissions,
  useLatestDashboardRun,
  usePrefetchAgentThreads,
  useRefreshDashboardMutation,
  useDashboardRunStatus,
  useSaveDashboardConfigMutation,
  presignBlockScreenshotUpload,
  registerBlockScreenshotUpload,
  uploadToPresignedS3Post,
  SQLiteS3FetchError,
} from "./webDashboard";
import {
  BlockContext,
  ChatMode,
  ChatSidebarHandle,
  getBlockContext,
  WebDashboardChatSidebar,
} from "./WebDashboardChatSidebar";
import { WebDashboardContent } from "./WebDashboardContent";
import { WebDashboardNavBar } from "./WebDashboardNavBar";
import { addPerfBreadcrumb, useFullLoadTransaction } from "./webDashboardPerf";
import { WebDashboardTrialStartBanner } from "./WebDashboardTrialStartBanner";
import { WebDashboardUpgradeModal } from "./WebDashboardUpgradeModal";
import { DashboardMode, WebDashboardViewerContext } from "./WebDashboardViewerContext";

// Note: overscroll-contain prevents odd overscroll bounce behavior (at least on macOS Chrome),
// where the main document and the iframed document *both* bounce, but slightly out of sync
// with each other.
// The inline font-size: 0.875rem (14px) pins the rem baseline independently of the text-sm
// class, whose --text-sm is pinned to 13px in applyTheme.ts (so nested text-sm reads 13px
// without shrinking this root/baseline). See the @theme comment in TAILWIND_STYLE_TAG.
const iframeSrcDoc = `
<!DOCTYPE html>
<html class="text-sm overscroll-contain bg-page font-themed" style="font-size: 0.875rem; scrollbar-gutter: stable; overflow: auto;">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <script src="https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4"></script>
  ${TAILWIND_STYLE_TAG}
  <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">
  <link href="https://fonts.googleapis.com/icon?family=Material+Icons+Outlined" rel="stylesheet">
  <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Fira+Sans:wght@400;500;600;700&family=Funnel+Display:wght@300;400;500;600;700&family=Google+Sans+Flex:wght@400;500;600;700&family=Inter:wght@400;500;600;700&family=Lexend:wght@400;500;600;700&family=Merriweather:wght@400;700&family=Noticia+Text:wght@400;700&family=Nunito:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&family=Quicksand:wght@400;500;600;700&family=Raleway:wght@400;500;600;700&family=Rubik:wght@400;500;600;700&family=Varela+Round&display=swap" rel="stylesheet">
  <!-- proxima-nova for the portaled nav UI (theme selector Advanced section, per Figma); same kit as the parent app. -->
  <link rel="stylesheet" href="https://use.typekit.net/zhk5lfr.css">
  <link href="https://unpkg.com/gridjs/dist/theme/mermaid.min.css" rel="stylesheet">
  <link href="https://cdn.jsdelivr.net/npm/flatpickr/dist/flatpickr.min.css" rel="stylesheet">
  ${FLATPICKR_STYLE_TAG}
  ${METRIC_STATUS_STYLE_TAG}
  <script src="https://code.jquery.com/jquery-3.7.1.min.js"
    integrity="sha384-1H217gwSVyLSIfaLxHbE7dRb3v4mYCKbpQvzx0cegeju1MVsGrX5xXxAvs/HgeFs"
    crossorigin="anonymous"></script>
  <!-- Pinned: sizeDataTableGrid and the frozen-column divider read AG Grid's internal DOM
       (.ag-center-cols-viewport / .ag-center-cols-container / .ag-floating-bottom), which v36
       renamed wholesale — this tag was unpinned and drifted there, silently breaking table
       sizing (last row hidden under always-on horizontal scrollbars). Keep in sync with the
       ag-grid-community version in webapp/package.json; recompute integrity on bump:
       curl -s <url> | openssl dgst -sha384 -binary | openssl base64 -A -->
  <script src="https://cdn.jsdelivr.net/npm/ag-grid-community@34.3.1/dist/ag-grid-community.min.js"
    integrity="sha384-1m5FM32cqWvkVPC2kdiP5dLsQUO3OCeEAGgZ4RqzCWlsWcCVIM9HXAjD1A99GPs3"
    crossorigin="anonymous"></script>
  <script src="https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js"></script>
  <script src="https://cdn.jsdelivr.net/npm/chartjs-plugin-datalabels@2"></script>
  <script src="https://cdn.jsdelivr.net/npm/chartjs-plugin-annotation@3"></script>
  <script src="https://cdn.jsdelivr.net/npm/hammerjs@2.0.8"></script>
  <script src="https://cdn.jsdelivr.net/npm/chartjs-plugin-zoom@2"></script>
  <script src="https://cdn.jsdelivr.net/npm/chartjs-adapter-date-fns@3"></script>
  <script src="https://cdn.jsdelivr.net/npm/chartjs-chart-funnel@4.2.5/build/index.umd.min.js"></script>
  <script src="https://cdn.jsdelivr.net/npm/chartjs-chart-treemap@3.1.0/dist/chartjs-chart-treemap.min.js"></script>
  <script src="https://cdn.jsdelivr.net/npm/chartjs-chart-sankey@0.14.0/dist/chartjs-chart-sankey.min.js"></script>
  <script src="https://cdn.jsdelivr.net/npm/chartjs-chart-matrix@2.0.1/dist/chartjs-chart-matrix.min.js"></script>
  <!-- ChartGeo global; bundles topojson-client (ChartGeo.topojson). Boundary atlases are
       fetched by map blocks at render time from pinned us-atlas@3.0.1 / world-atlas@2.0.2. -->
  <script src="https://cdn.jsdelivr.net/npm/chartjs-chart-geo@4.3.6/build/index.umd.min.js"></script>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/sql.js/1.5.0/sql-wasm.js"></script>
  <script src="https://cdn.jsdelivr.net/npm/flatpickr@4.6"></script>
  <script src="https://cdn.jsdelivr.net/npm/marked@17/lib/marked.umd.js"></script>
  <!-- html2canvas 1.4.1 does not support CSS Color 4 functions like oklab()/oklch() (common with Tailwind v4).
       Use html2canvas-pro which supports oklab/oklch parsing. -->
  <script src="https://cdn.jsdelivr.net/npm/html2canvas-pro@1.6.7/dist/html2canvas-pro.min.js"></script>
  <style>
    ::view-transition-old(root),
    ::view-transition-new(root) {
      animation-duration: 200ms;
    }

    .max-w-8xl { max-width: 88rem; }
    .max-w-9xl { max-width: 96rem; }
    .w-8xl { width: 88rem; }
    .w-9xl { width: 96rem; }

    header h1,
    main h2 {
      scroll-margin-top: 100px;
    }

    .disable-transitions * {
      transition: none !important;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .loading-spinner {
      animation: spin 1s linear infinite;
    }

    /* The mobile title and block-description disclosures are native details/summary elements (the
       previous focus-based reveal could not be opened on iOS at all). Strip the default marker
       triangle so only the info icon and the title text show. Lives here rather than in a Tailwind
       arbitrary variant because this stylesheet is synchronous, while Tailwind arrives via an async
       CDN JIT — a class-based reset would let the triangle flash on first paint. Scoped to the title
       row, which contains both disclosures. */
    .block-title-row summary {
      list-style: none;
      cursor: pointer;
    }
    .block-title-row summary::-webkit-details-marker {
      display: none;
    }

    /* Two-layer shadow with the geometry from Figma 5753:14025 (0 0 16px ambient + 0 16px 24px
       directional), but black at low alpha rather than the design's opaque Gray/200. An opaque grey
       reads as a flat halo ringing the popover rather than depth, and it inverts on the dark themes:
       a light shadow on a dark surface glows outward instead of receding. Black alpha darkens
       whatever is behind it, so one declaration works on every theme -- which is also why this no
       longer needs a theme token or its unset fallback.
       Still plain CSS rather than a Tailwind shadow-[...] arbitrary value: the comma-separated
       two-layer form silently failed to compile, leaving Tailwind's empty --tw-shadow placeholder
       chain and therefore no shadow at all. Shared by the title and description popovers, which are
       styled identically. */
    .block-popover {
      box-shadow:
        0 0 16px rgba(0, 0, 0, 0.06),
        0 16px 24px rgba(0, 0, 0, 0.12);
    }

    @keyframes ai-btn-gradient-shift {
      0% { background-position: 0% 50%; }
      100% { background-position: 100% 50%; }
    }

    .thinking-text {
      background: linear-gradient(90deg, #6b7280 0%, #9ca3af 25%, #6b7280 50%, #9ca3af 75%, #6b7280 100%);
      background-size: 200% 100%;
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      animation: shimmer 2s ease-in-out infinite;
    }

    @keyframes shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(4px); }
      to { opacity: 1; transform: translateY(0); }
    }

    .skeleton-fade-out {
      animation: fadeOut 0.25s ease-out forwards;
    }

    @keyframes fadeOut {
      from { opacity: 1; }
      to { opacity: 0; }
    }

    .block-fade-in {
      animation: fadeIn 0.25s ease-out forwards;
    }

    /* Intentionally no shimmer overlay or dimming while regenerating; keep the stale image static. */

    /* Glowing rim effect for regenerating AI image cards, mimicking the dashboard "Edit" button.
       Key detail: animate ONLY a pseudo-element (the rim), not the wrapper itself, so the
       underlying card/image never rotates.

       Implementation notes:
       - The spinning conic-gradient is intentionally larger than the wrapper and centered,
         so its motion feels smooth and doesn't look "cut off".
       - The inner panel uses a slightly smaller radius so the rim thickness is consistent
         and doesn't show odd gaps from anti-aliasing on very thin borders. */
    .ai-image-regenerating-wrapper,
    .block-refreshing-wrapper {
      position: relative;
      box-sizing: content-box;
      margin: -2px;
      padding: 2px;
      border-radius: calc(var(--radius-themed) + 2px);
      overflow: hidden;
      z-index: 0;
    }

    .ai-image-regenerating-wrapper > .themed-dashboard-card,
    .block-refreshing-wrapper > .themed-dashboard-card {
      position: relative;
      z-index: 2;
      height: 100%;
    }

    /* Some blocks (e.g. AIInsightsBlock) don't use themed-dashboard-card on their root.
       Ensure the wrapped block content is always above the rim layers. */
    .block-refreshing-wrapper > * {
      position: relative;
      z-index: 2;
      height: 100%;
    }

    .ai-image-regenerating-wrapper::after,
    .block-refreshing-wrapper::after {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      width: 160%;
      aspect-ratio: 1 / 1;
      background: conic-gradient(#22d3ee,#355fcd,#3c49ea,#8b5cf6,#f04078,#f78c3a,#fbbf24,#22d3ee);
      animation: ai-image-rim-spin 4s linear infinite;
      z-index: 0;
      pointer-events: none;
      opacity: 0.95;
      filter: blur(1.5px);
    }

    .ai-image-regenerating-wrapper::before,
    .block-refreshing-wrapper::before {
      content: "";
      position: absolute;
      inset: 2px;
      border-radius: var(--radius-themed);
      background: var(--color-card-background);
      z-index: 1;
      pointer-events: none;
    }

    @keyframes ai-image-rim-spin {
      from { transform: translate(-50%, -50%) rotate(0deg); }
      to { transform: translate(-50%, -50%) rotate(360deg); }
    }
  </style>
</head>
<body>
  <div id="iframe-root" class="disable-transitions"></div>
</body>
</html>
`;

type DashboardBuildPhase =
  | typeof WebDashboardBuildingPhase.Started
  | typeof WebDashboardBuildingPhase.ExploringData
  | typeof WebDashboardBuildingPhase.PlanningLayout
  | typeof WebDashboardBuildingPhase.BuildingDashboard
  | typeof WebDashboardBuildingPhase.Completed
  | typeof WebDashboardBuildingPhase.Failed;

type DashboardLifecycleMode = "create" | "edit";

const DASHBOARD_BUILD_CONFETTI_MS = 6500;

// How long the "first_dashboard_created" attribution iframe stays mounted. It has to outlive the
// GA4 / Meta pixel loads inside it, so this matches the add-on's EventAttributionIframeProvider.
const FIRST_DASHBOARD_ATTRIBUTION_IFRAME_MS = 10_000;

// Identifies the theme Reset toast so a browse back to the opening state can dismiss just that one.
const THEME_TOAST_MESSAGE = "Theme updated";

const getDashboardDataBlockIds = (contentSpec?: WebDashboardContentSpec): string[] =>
  contentSpec?.blocks
    .filter(b => b.type === "metric_card" || b.type === "chart" || b.type === "data_table")
    .map(b => b.block_id) ?? [];

const getDashboardLifecycleMode = (phase?: DashboardBuildPhase | null): DashboardLifecycleMode => {
  // Existing dashboards may not have a build phase; only known non-completed
  // phases represent the create/build flow.
  if (!phase) {
    return "edit";
  }
  return phase === WebDashboardBuildingPhase.Completed ? "edit" : "create";
};

const usePrevious = <T,>(value: T): T | undefined => {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
};

/**
 * Convert block type to user-friendly label for display in toasts
 */
function getBlockTypeLabel(blockType: string | undefined): string {
  switch (blockType) {
    case "chart":
      return "Chart";
    case "data_table":
      return "Table";
    case "metric_card":
      return "Metric Card";
    case "ai_image":
      return "AI Image";
    case "ai_insights":
      return "AI Insights Card";
    case "section_header":
      return "Section Header";
    case "text_block":
      return "Text Block";
    default:
      return "Block";
  }
}

interface RefreshSnackbarProps {
  liftForBottomToast?: boolean;
  startedMs?: number | null;
  // Width (px) of the right-side chat sidebar when open. The snackbar shifts
  // left by half this value so it stays visually centered over the dashboard
  // content area instead of the full viewport.
  sidebarOffset?: number;
}

interface CheckingChangesSnackbarProps {
  liftForBottomToast?: boolean;
  sidebarOffset?: number;
}

// Centers the snackbar over the dashboard content area: when the chat sidebar
// is open, the available content width is `viewport - sidebarOffset`, so the
// snackbar shifts left by half the sidebar width.
const snackbarCenterTransform = (sidebarOffset: number) =>
  `translateX(calc(-50% - ${sidebarOffset / 2}px))`;

const CheckingChangesSnackbar: React.FC<CheckingChangesSnackbarProps> = ({
  liftForBottomToast = false,
  sidebarOffset = 0,
}) => {
  return (
    <div
      className={
        "fixed left-1/2 " +
        (liftForBottomToast ? "bottom-20 " : "bottom-6 ") +
        // Keep snackbar below toast z-index so it never covers toast actions (e.g. Undo).
        "z-[900] transition-transform duration-300 ease-in-out"
      }
      style={{ transform: snackbarCenterTransform(sidebarOffset) }}
    >
      <div className="flex items-center gap-2 rounded-xl bg-card-background shadow-card border border-card-border px-4 py-2">
        {/* Coeff AI spinner (same as RefreshSnackbar) */}
        <div className="relative w-6 h-6">
          {/* Spinning gradient arc */}
          <svg
            className="absolute inset-0 w-full h-full loading-spinner"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="coeff-ai-spinner-grad-checking" x1="4" y1="4" x2="20" y2="20">
                <stop
                  offset="0%"
                  stopColor="var(--theme--color-primary-button-background, #3b82f6)"
                  stopOpacity="0.35"
                />
                <stop
                  offset="55%"
                  stopColor="var(--theme--color-primary-button-background, #3b82f6)"
                  stopOpacity="0.75"
                />
                <stop
                  offset="100%"
                  stopColor="var(--theme--color-primary-button-background, #3b82f6)"
                  stopOpacity="1"
                />
              </linearGradient>
            </defs>
            <circle
              cx="12"
              cy="12"
              r="9"
              stroke="url(#coeff-ai-spinner-grad-checking)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="36 20"
              strokeDashoffset="6"
            />
          </svg>

          {/* Static sparkle */}
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M12 6.2l1.9 4.3L18.2 12l-4.3 1.5L12 17.8l-1.9-4.3L5.8 12l4.3-1.5L12 6.2z"
              fill="var(--theme--color-primary-button-background, #3b82f6)"
            />
          </svg>
        </div>
        <div className="text-sm font-medium text-card-foreground">Checking changes…</div>
      </div>
    </div>
  );
};

const RefreshSnackbar: React.FC<RefreshSnackbarProps> = ({
  liftForBottomToast = false,
  startedMs,
  sidebarOffset = 0,
}) => {
  const [secondsElapsed, setSecondsElapsed] = useState(0);

  useEffect(() => {
    if (startedMs == null) {
      setSecondsElapsed(0);
      return;
    }
    setSecondsElapsed(Math.max(0, Math.floor((Date.now() - startedMs) / 1000)));
    const intervalId = setInterval(() => {
      setSecondsElapsed(Math.max(0, Math.floor((Date.now() - startedMs) / 1000)));
    }, 1000);
    return () => clearInterval(intervalId);
  }, [startedMs]);

  const label = getRefreshPhaseLabel(secondsElapsed);

  return (
    <div
      className={
        "fixed left-1/2 " +
        (liftForBottomToast ? "bottom-20 " : "bottom-6 ") +
        // Keep snackbar below toast z-index so it never covers toast actions (e.g. Undo).
        "z-[900] transition-transform duration-300 ease-in-out"
      }
      style={{ transform: snackbarCenterTransform(sidebarOffset) }}
    >
      <div className="flex items-center gap-2 rounded-xl bg-card-background shadow-card border border-card-border px-4 py-2">
        {/* Coeff AI spinner */}
        <div className="relative w-6 h-6">
          {/* Spinning gradient arc */}
          <svg
            className="absolute inset-0 w-full h-full loading-spinner"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="coeff-ai-spinner-grad" x1="4" y1="4" x2="20" y2="20">
                <stop
                  offset="0%"
                  stopColor="var(--theme--color-primary-button-background, #3b82f6)"
                  stopOpacity="0.35"
                />
                <stop
                  offset="55%"
                  stopColor="var(--theme--color-primary-button-background, #3b82f6)"
                  stopOpacity="0.75"
                />
                <stop
                  offset="100%"
                  stopColor="var(--theme--color-primary-button-background, #3b82f6)"
                  stopOpacity="1"
                />
              </linearGradient>
            </defs>
            <circle
              cx="12"
              cy="12"
              r="9"
              stroke="url(#coeff-ai-spinner-grad)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="36 20"
              strokeDashoffset="6"
            />
          </svg>

          {/* Static sparkle */}
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M12 6.2l1.9 4.3L18.2 12l-4.3 1.5L12 17.8l-1.9-4.3L5.8 12l4.3-1.5L12 6.2z"
              fill="var(--theme--color-primary-button-background, #3b82f6)"
            />
          </svg>
        </div>
        <div className="text-sm font-medium text-card-foreground">{label}</div>
      </div>
    </div>
  );
};

export const WebDashboardViewer: React.FC = () => {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const history = useHistory();
  const location = useLocation();

  const { instance: msalInstance } = useMsal();

  const track = useTrack();
  const { apiClient, DEPRECATED_api } = useApiContext();
  const queryClient = useQueryClient();

  const [iframeBody, setIframeBody] = useState<HTMLElement | null>(null);

  const [iframeWindow, setIframeWindow] = useState<DashboardWindow | null>(null);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const hasTrackedViewRef = useRef(false);
  const hasRecordedViewRef = useRef(false);

  // The top-level <meta viewport> this route needs is installed by useDashboardViewportMeta() in
  // WebDashboardViewerWithStatsig — it has to be applied before the route's loading/error states
  // paint, which happens before this component mounts.

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (iframe?.contentWindow?.document?.body) {
      const root = iframe.contentWindow.document.getElementById("iframe-root");
      if (root) {
        setIframeBody(root);
        setIframeWindow(iframe.contentWindow as DashboardWindow);
      }
    }
  };

  // Check if view_as_super_admin query param is present (from super admin redirect)
  // Must be computed before useDashboard since we need to pass it to the hook
  const requestSuperAdminView = new URLSearchParams(location.search).has("view_as_super_admin");
  const createModeDashboardPollStartTimeRef = useRef<number | null>(null);

  const {
    data: dashboardData,
    isLoading: isLoadingDashboard,
    error: dashboardError,
  } = useDashboard(dashboardId, null, requestSuperAdminView, {
    refetchInterval: data => {
      if (requestSuperAdminView) {
        createModeDashboardPollStartTimeRef.current = null;
        return false;
      }

      if (getDashboardLifecycleMode(data?.building_phase) !== "create") {
        createModeDashboardPollStartTimeRef.current = null;
        return false;
      }

      if (createModeDashboardPollStartTimeRef.current === null) {
        createModeDashboardPollStartTimeRef.current = Date.now();
      }

      if (
        Date.now() - createModeDashboardPollStartTimeRef.current >=
        CREATE_MODE_DASHBOARD_POLL_TIMEOUT_MS
      ) {
        return false;
      }

      return 2000;
    },
    refetchIntervalInBackground: true,
  });

  // Determine whether AI image blocks are potentially stale relative to the last time
  // they were regenerated.
  const areAiImagesStale = areAiImagesStaleForDashboard(
    dashboardData as any /* may not yet include timestamp fields in TS types */
  );

  // When AI images are stale, poll just the dashboard JSON in the background and update
  // the cached WEB_DASHBOARD query once images are fresh. This avoids repeatedly
  // re-rendering the entire page while still picking up new image_url values.
  useEffect(() => {
    if (!dashboardId || !areAiImagesStale || requestSuperAdminView) {
      return;
    }

    const POLL_INTERVAL_MS = 2000;
    const MAX_POLL_DURATION_MS = 5 * 60 * 1000; // 5 minutes
    const startTime = Date.now();

    let cancelled = false;
    const intervalId = window.setInterval(async () => {
      if (cancelled) {
        return;
      }

      const elapsed = Date.now() - startTime;
      if (elapsed >= MAX_POLL_DURATION_MS) {
        // Stop polling after max duration even if images are still stale.
        window.clearInterval(intervalId);
        return;
      }

      try {
        const response = await apiClient.getWebDashboard({
          web_dashboard_id: dashboardId,
        });
        const fresh = response.data as GetWebDashboard200Response & {
          data_last_changed_dt?: string;
          ai_images_last_generated_dt?: string;
        };

        const stillStale = areAiImagesStaleForDashboard(fresh);

        if (!stillStale) {
          // Update the main WEB_DASHBOARD cache once, so the viewer (and AI image
          // blocks) pick up the new image URLs without a full reload.
          queryClient.setQueryData<GetWebDashboard200Response>(
            [QueryCacheKey.WEB_DASHBOARD, dashboardId],
            fresh
          );
          window.clearInterval(intervalId);
        }
      } catch (error) {
        // Log and keep trying; transient failures shouldn't permanently break image refresh.
        console.error("Error polling for refreshed AI images", error);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [apiClient, dashboardId, areAiImagesStale, queryClient, requestSuperAdminView]);

  // Extract slicer names for analytics
  const slicerNames = useMemo(() => {
    return (
      dashboardData?.content_config?.content_spec?.slicers?.map((s: { name: string }) => s.name) ||
      []
    );
  }, [dashboardData]);

  // True when the dashboard has at least one personalized view slicer
  const hasPersonalizedViews = (dashboardData?.content_config?.content_spec?.slicers ?? []).some(
    (s: { type?: string }) => s.type === "personalized_view"
  );

  const mountTimeRef = useRef(Date.now());
  const dbLoadMsRef = useRef<number | null>(null);
  const isPublicDashboard = dashboardData?.sharing_level === "public";
  const { data: permissions, isLoading: isLoadingPermissions } = useDashboardPermissions(
    dashboardId,
    requestSuperAdminView
  );
  const shouldWaitForPermissionsBeforeSqliteInit = isPublicDashboard && isLoadingPermissions;
  const usePublicSqliteDownload =
    isPublicDashboard && !isLoadingPermissions && permissions?.is_authenticated === false;
  const hasDashboardSqliteData = Boolean(
    dashboardData?.spreadsheet_version_id || (dashboardData?.tabs?.length ?? 0) > 0
  );

  const {
    runSQL,
    isInitialized: isSQLiteInitializedFromHook,
    dbLoadMs,
    error: sqliteError,
  } = useRunSQL({
    spreadsheetId:
      shouldWaitForPermissionsBeforeSqliteInit || !hasDashboardSqliteData
        ? null
        : dashboardData?.spreadsheet_id || null,
    spreadsheetVersionId: dashboardData?.spreadsheet_version_id,
    dashboardId: hasDashboardSqliteData ? dashboardId : undefined,
    combinedTablesHash: dashboardData?.combined_tables_hash,
    apiClient,
    isPublicDashboard: usePublicSqliteDownload,
    requestSuperAdminView,
  });

  // Keep dbLoadMsRef in sync so handleBlockRendered always reads the latest value
  // without needing dbLoadMs in its useCallback deps.
  useEffect(() => {
    dbLoadMsRef.current = dbLoadMs;
  }, [dbLoadMs]);

  // Detect when the SQLite DB starts reloading (isInitialized transitions true → false)
  // and show a toast so the user knows data is refreshing.
  const prevSQLiteInitializedRef = useRef(false);
  useEffect(() => {
    if (prevSQLiteInitializedRef.current && !isSQLiteInitializedFromHook) {
      const isCreateBuildFlow =
        getDashboardLifecycleMode(dashboardData?.building_phase) === "create" &&
        !requestSuperAdminView;
      // During AI dashboard creation, combined_tables_hash updates as tabs are added to
      // web_dashboard_json; SQLite legitimately reloads but that is not a user "data change"
      // and the toast is distracting / alarming while the build loader is already shown.
      if (!isCreateBuildFlow) {
        setToastProps({
          message: "Detected data changes, refreshing...",
          type: "info",
          materialIcon: "refresh",
          duration: 3000,
          onClose: () => setToastProps(null),
        });
      }
    }
    prevSQLiteInitializedRef.current = isSQLiteInitializedFromHook;
  }, [dashboardData?.building_phase, isSQLiteInitializedFromHook, requestSuperAdminView]);

  // Check if super admin access was granted (read-only mode) - from API response
  const hasSuperAdminAccess = permissions?.is_super_admin_view || false;
  const superAdminExpirationDt = permissions?.super_admin_access_expiration_dt;

  // Super admin view is always read-only, even if permissions.can_edit is true
  const canEditDashboard = !hasSuperAdminAccess && !!permissions?.can_edit;

  // Current user's plan type from client flags (undefined for unauthenticated/public viewers).
  const clientFlagsPlanType = useSelector((state: AppState) => state.app.clientFlags?.plan_type);

  // Archived dashboards disable AI chat entirely (refresh stays clickable but the backend
  // returns an "archived" status that surfaces a toast).
  const isArchivedDashboard = Boolean(
    (dashboardData as { is_archived?: boolean } | undefined)?.is_archived
  );

  const ownerWebDashboardTrial = permissions?.owner_web_dashboard_trial;
  const trialExpired = isWebDashboardTrialExpired(ownerWebDashboardTrial);
  const isAuthenticated = permissions?.is_authenticated || false;
  const userEmail = permissions?.user_email || null;
  const userName = useSelector((state: AppState) => state.app.name);
  const ownerEmail = permissions?.owner_email || null;

  const trialDaysRemaining = (() => {
    const isCountdown =
      ownerWebDashboardTrial?.trial_state === WebDashboardTrialStateType.TrialManualCountdown ||
      ownerWebDashboardTrial?.trial_state === WebDashboardTrialStateType.TrialCountdown;
    if (!isCountdown || !ownerWebDashboardTrial?.trial_state_end_dt) {
      return null;
    }
    return Math.max(
      0,
      Math.ceil(
        (new Date(ownerWebDashboardTrial.trial_state_end_dt).getTime() - Date.now()) /
          (1000 * 60 * 60 * 24)
      )
    );
  })();
  // Early-trial announcement: owner only, while in countdown with more than the final urgency week
  // left (the nav bar shows the "N days left" urgency badge in the last 7 days).
  const showTrialStartBanner =
    canEditDashboard &&
    Boolean(permissions?.user_id) &&
    trialDaysRemaining !== null &&
    trialDaysRemaining > 7;
  // Only paid users get the "Learn More" (Contact Sales) link in the banner; free users just see
  // the announcement. Paid status comes from the current user's client-flags plan_type.
  const isPaidUser = isPaidPlanType(clientFlagsPlanType);

  // Upgrade CTAs (expired banner, trial badge) go straight to the user's self-serve destination
  // per the Oct 22 mocks - the subscription page with the AI Dashboards confirm modal open for
  // eligible Pro subscribers, the plan chooser for everyone else (ineligible users get contact
  // sales there). The modal is only a fallback while the offer loads. The offer is
  // fetched up front so the new tab opens inside the click (an async open gets popup-blocked).
  const { value: enableSelfServeAiDashboards } = useFeatureGate(
    FeatureFlag.EnableSelfServeAiDashboards
  );
  const { data: webDashboardOffer } = useWebDashboardOffer({
    enabled:
      enableSelfServeAiDashboards &&
      canEditDashboard &&
      (trialExpired || trialDaysRemaining !== null),
  });
  const openUpgrade = (variant: "expired" | "proactive", openModal: () => void) => {
    const destination = getWebDashboardUpgradeDestination(
      webDashboardOffer?.web_dashboard_eligibility
    );
    if (!enableSelfServeAiDashboards || !webDashboardOffer) {
      openModal();
      return;
    }
    const from = WEB_DASHBOARD_UPGRADE_MODAL_SOURCES[variant];
    // Gate-tagged so the click records as upgrade_clicked in the shared gate funnel (ENG-9327).
    track(AnalyticsEventNameType.VibeDashUpgradeModalViewPlansClicked, {
      vibe_dash_id: dashboardId,
      vibe_dash_upgrade_modal_variant: variant,
      event_from: from,
      ...getAiDashboardsGateProps(variant),
    });
    window.open(
      destination === "add_on_modal" ? getAddAiDashboardsUrl(from) : getPlanChooserUrl(from),
      "_blank"
    );
  };

  // Unified tracking helper that routes to authenticated or anonymous tracking
  const trackDashboardEvent = useCallback(
    (eventName: AnalyticsEventNameType, props: AnalyticsEvent) => {
      if (isAuthenticated) {
        track(eventName, props);
      } else {
        analyticsTrackAnonymous(apiClient, eventName, {
          ...props,
          temp_user_key: getAnonymousUserId(),
        });
      }
    },
    [isAuthenticated, track, apiClient]
  );

  // Set Sentry user when dashboard permissions load with user email
  useEffect(() => {
    if (userEmail) {
      Sentry.setUser({ email: userEmail });
    }
  }, [userEmail]);

  const loading = isLoadingDashboard || isLoadingPermissions;
  const dashboardBuildPhase = dashboardData?.building_phase;
  const dashboardLifecycleMode = getDashboardLifecycleMode(dashboardBuildPhase);
  const isDashboardCreateMode = dashboardLifecycleMode === "create" && !requestSuperAdminView;
  // A crashed build (set terminal FAILED by the backend crash handler). Terminal, not "building":
  // we must not show the progress loader for it, or it looks perpetually in progress.
  const isDashboardBuildFailed =
    !requestSuperAdminView && dashboardBuildPhase === WebDashboardBuildingPhase.Failed;

  const dashboardContentSpec = dashboardData?.content_config?.content_spec as
    | WebDashboardContentSpec
    | undefined;
  const dashboardBuildDataBlockIds = useMemo(
    () => getDashboardDataBlockIds(dashboardContentSpec),
    [dashboardContentSpec]
  );
  const [showDashboardBuildConfetti, setShowDashboardBuildConfetti] = useState(false);
  const shouldShowDashboardBuildConfettiAfterRenderRef = useRef(false);
  const previousDashboardBuildPhase = usePrevious(dashboardBuildPhase);
  const didCompleteDashboardBuild =
    !requestSuperAdminView &&
    dashboardBuildPhase === WebDashboardBuildingPhase.Completed &&
    Boolean(previousDashboardBuildPhase) &&
    previousDashboardBuildPhase !== WebDashboardBuildingPhase.Completed;

  useEffect(() => {
    if (requestSuperAdminView || isDashboardCreateMode) {
      setShowDashboardBuildConfetti(false);
      shouldShowDashboardBuildConfettiAfterRenderRef.current = false;
      return;
    }

    if (!didCompleteDashboardBuild) {
      return;
    }

    if (dashboardBuildDataBlockIds.length === 0) {
      setShowDashboardBuildConfetti(true);
      return;
    }

    shouldShowDashboardBuildConfettiAfterRenderRef.current = true;
    if (dashboardBuildDataBlockIds.every(id => renderedBlockIdsRef.current.has(id))) {
      shouldShowDashboardBuildConfettiAfterRenderRef.current = false;
      setShowDashboardBuildConfetti(true);
    }
  }, [
    dashboardBuildDataBlockIds,
    didCompleteDashboardBuild,
    isDashboardCreateMode,
    requestSuperAdminView,
  ]);

  useEffect(() => {
    if (!showDashboardBuildConfetti) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShowDashboardBuildConfetti(false);
    }, DASHBOARD_BUILD_CONFETTI_MS);

    return () => window.clearTimeout(timeoutId);
  }, [showDashboardBuildConfetti]);

  // Paid-attribution event (GA4 + Meta CAPI) for the user's first completed dashboard. Fired
  // once, on the build-completion transition, and latched in a ref because
  // didCompleteDashboardBuild is derived state that flips back to false on the next render.
  // Re-fire is guarded on three sides: the transition requires having observed a non-completed
  // phase in this mount (so reopening a finished dashboard is inert), is_first_web_dashboard is
  // stamped once server-side at build completion, and the backend only ever returns it true to
  // the dashboard's owner. See docs/domains/concepts/growth-attribution.md.
  const [showFirstDashboardAttributionIframe, setShowFirstDashboardAttributionIframe] =
    useState(false);
  const hasFiredFirstDashboardAttributionRef = useRef(false);
  const isFirstWebDashboard = Boolean(dashboardData?.is_first_web_dashboard);

  useEffect(() => {
    if (
      !didCompleteDashboardBuild ||
      hasFiredFirstDashboardAttributionRef.current ||
      !isFirstWebDashboard ||
      !isAuthenticated ||
      !userEmail
    ) {
      return;
    }

    hasFiredFirstDashboardAttributionRef.current = true;
    setShowFirstDashboardAttributionIframe(true);
  }, [didCompleteDashboardBuild, isFirstWebDashboard, isAuthenticated, userEmail]);

  // Unmount timer lives in its own effect, keyed on the visibility state rather than on
  // didCompleteDashboardBuild: that flag flips back to false on the very next render, which would
  // run this cleanup and cancel the timer, leaving the iframe mounted for the rest of the session.
  useEffect(() => {
    if (!showFirstDashboardAttributionIframe) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShowFirstDashboardAttributionIframe(false);
    }, FIRST_DASHBOARD_ATTRIBUTION_IFRAME_MS);

    return () => window.clearTimeout(timeoutId);
  }, [showFirstDashboardAttributionIframe]);

  // The web dashboard trial is created by the backend during the first build, but the
  // permissions query (which carries owner_web_dashboard_trial, the source for the trial
  // banner) is fetched once on mount — before the trial exists. Refetch it when the build
  // completes so the trial banner appears without a manual browser refresh.
  useEffect(() => {
    if (!didCompleteDashboardBuild || !dashboardId) {
      return;
    }
    queryClient.invalidateQueries([
      QueryCacheKey.WEB_DASHBOARD_PERMISSIONS,
      dashboardId,
      requestSuperAdminView,
    ]);
  }, [didCompleteDashboardBuild, dashboardId, requestSuperAdminView, queryClient]);

  // A failed build is terminal: show the failure panel, not the build progress loader.
  const shouldShowDashboardBuildLoader =
    isDashboardCreateMode && !dashboardContentSpec && !isDashboardBuildFailed;
  const shouldShowDashboardBuildFailed = isDashboardBuildFailed && !dashboardContentSpec;

  // ENG-9327: the "Dashboard trial expired" banner is the AI Dashboards paywall's shown step. Only
  // for editors (the owner-facing copy with Upgrade); viewers see "no longer refreshing" with no
  // CTA. The server keeps one row per user per day, so a re-render here is harmless.
  const showsExpiredTrialPaywall =
    trialExpired && canEditDashboard && isAuthenticated && !shouldShowDashboardBuildLoader;
  useEffect(() => {
    if (!showsExpiredTrialPaywall) {
      return;
    }
    track(AnalyticsEventNameType.FeaturePaywallShown, {
      vibe_dash_id: dashboardId,
      ...getAiDashboardsGateProps("expired"),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showsExpiredTrialPaywall, dashboardId]);
  const shouldShowBuilderChatSidebar = isDashboardCreateMode && Boolean(dashboardData);
  const shouldShowDashboardBuildConfetti = showDashboardBuildConfetti;
  const dashboardBuildProgressPhase =
    shouldShowDashboardBuildLoader &&
    dashboardBuildPhase === WebDashboardBuildingPhase.BuildingDashboard
      ? WebDashboardBuildingPhase.PlanningLayout
      : dashboardBuildPhase;

  const isAccessDenied = isAxiosError(dashboardError) && dashboardError.response?.status === 403;
  const isNotFound = isAxiosError(dashboardError) && dashboardError.response?.status === 404;

  const error = !dashboardId
    ? "Dashboard ID is required"
    : isNotFound
    ? "Dashboard not found"
    : isAccessDenied
    ? "This dashboard is only accessible to users from the same organization as the creator. Please sign in with an account from the correct organization."
    : dashboardError
    ? dashboardError instanceof Error
      ? dashboardError.message
      : "Failed to load dashboard"
    : null;

  const [isChatSidebarOpen, setIsChatSidebarOpen] = useState(false);
  const [isChatUnavailableModalOpen, setIsChatUnavailableModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isProactiveUpgradeModalOpen, setIsProactiveUpgradeModalOpen] = useState(false);
  const [isTrialInfoModalOpen, setIsTrialInfoModalOpen] = useState(false);
  const isEffectiveChatSidebarOpen = isDashboardCreateMode || isChatSidebarOpen;
  const chatSidebarRef = useRef<ChatSidebarHandle>(null);
  const [sidebarWidth, setSidebarWidth] = useState(353);
  const [isSidebarResizing, setIsSidebarResizing] = useState(false);
  // Below 640px the chat sidebar is `max-sm:hidden`, so it occupies no layout space — but
  // `isChatSidebarOpen` is React state and survives a resize, so anything that offsets content by
  // the sidebar's width would reserve room for something that isn't rendered. (ChatSidebarFlexSpacer
  // already handles its own case with a `max-sm:!w-0` override; these consumers set inline styles,
  // which no class can reach.) Treat the width as zero on mobile and use this everywhere the
  // sidebar's footprint matters.
  const isMobileViewport = useIsMobile(iframeWindow);
  const effectiveSidebarWidth = isMobileViewport ? 0 : sidebarWidth;
  // Dismisses the mobile title/description popovers on a tap outside them. Not gated on
  // isMobileViewport: the disclosures only exist below 640px anyway, and the listener no-ops when
  // none are open, so gating would only risk the popover surviving a resize with no way to close it.
  useCloseBlockPopoversOnOutsideTap(iframeWindow);
  const [isThemeSelectorOpen, setIsThemeSelectorOpen] = useState(false);
  const [isGoogleSheetsAuthModalOpen, setIsGoogleSheetsAuthModalOpen] = useState(false);

  const [googleOAuthErrorReason, setGoogleOAuthErrorReason] =
    useState<GoogleOAuthErrorReason>("token_expired");
  const [isMicrosoftAuthModalOpen, setIsMicrosoftAuthModalOpen] = useState(false);
  const [isChatOAuthRequired, setIsChatOAuthRequired] = useState(false);
  const [shouldRetryPendingRefreshAfterOAuth, setShouldRetryPendingRefreshAfterOAuth] =
    useState(false);
  const [shouldRetryPendingChatAfterOAuth, setShouldRetryPendingChatAfterOAuth] = useState(false);
  const [toastProps, setToastProps] = useState<ToastProps | null>(null);
  const oauthRedirectPath = useMemo(() => {
    const params = new URLSearchParams(location.search);
    params.delete("oauth_success");
    params.delete("oauth_cancelled");
    params.delete("oauth_error");
    const qs = params.toString();
    return `${location.pathname}${qs ? `?${qs}` : ""}`;
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (isDashboardCreateMode) {
      setIsChatSidebarOpen(true);
    }
  }, [isDashboardCreateMode]);

  const startViewTransition = useViewTransitionStateUpdate(iframeWindow?.document);

  // Theme state - initialized from dashboard config, defaults to 'light'
  const initialThemeId =
    (dashboardData?.content_config as { theme_id?: string } | undefined)?.theme_id ??
    DEFAULT_THEME_ID;
  // Register the resolved custom ThemeSpec (embedded in the GET response) so resolveTheme can
  // expand `custom:{uuid}` ids. Done during render (via useMemo) so it lands before the effects
  // that read the theme run. No-op for built-in ids or when no custom theme is resolved.
  const resolvedTheme = dashboardData?.resolved_theme as ThemeSpec | undefined;
  useMemo(() => {
    if (isCustomThemeId(initialThemeId) && resolvedTheme) {
      registerCustomThemeSpec(initialThemeId, resolvedTheme);
    }
  }, [initialThemeId, resolvedTheme]);
  const [themeId, setThemeId] = useState<string>(initialThemeId);
  // Palette-selector toggles (distinct from `dashboardMode`, the viewer/editor UI mode below).
  // Legacy dashboards persist no mode/background; seed the toggles from the legacy→palette map so
  // a dark legacy theme opens the selector with Dark ON — otherwise the first palette pick would
  // silently commit light mode and flip the dashboard.
  const legacyToggleSeed = LEGACY_THEME_TO_PALETTE[initialThemeId];
  const initialThemeMode =
    (dashboardData?.content_config as { mode?: ThemeMode } | undefined)?.mode ??
    legacyToggleSeed?.mode ??
    DEFAULT_THEME_MODE;
  const initialThemeBackground =
    (dashboardData?.content_config as { background?: ThemeBackground } | undefined)?.background ??
    legacyToggleSeed?.background ??
    DEFAULT_THEME_BACKGROUND;
  const [themeMode, setThemeMode] = useState<ThemeMode>(initialThemeMode);
  const [themeBackground, setThemeBackground] = useState<ThemeBackground>(initialThemeBackground);
  const [dashboardMode, setDashboardMode] = useState<DashboardMode>("viewer");
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [chatBlockContexts, setChatBlockContexts] = useState<BlockContext[]>([]);
  // Block IDs frozen at submit time — only these should glow during thinking
  const [submittedBlockContexts, setSubmittedBlockContexts] = useState<BlockContext[]>([]);
  const [agentInferredRefreshingBlockIds, setAgentInferredRefreshingBlockIds] = useState<
    Set<string>
  >(new Set());

  const availableBlocks = useMemo(
    () =>
      (dashboardData?.content_config?.content_spec as WebDashboardContentSpec | undefined)
        ?.blocks ?? [],
    [dashboardData?.content_config?.content_spec]
  );

  // Force a full dashboard runtime remount when slicers or temp views change from AI updates.
  // Those changes can require re-initializing runtime state, not just patching block props.
  const dashboardRuntimeRemountKey = useMemo(() => {
    const spec = dashboardData?.content_config?.content_spec as WebDashboardContentSpec | undefined;
    if (!spec) {
      return "runtime-none";
    }
    return JSON.stringify({
      slicers: spec.slicers ?? [],
      createTempViewSqlStatements: spec.create_temp_view_sql_statements ?? [],
    });
  }, [dashboardData?.content_config?.content_spec]);

  const handleAddBlockContext = useCallback((ctx: BlockContext) => {
    setChatBlockContexts(prev => {
      if (prev.some(existing => existing.blockId === ctx.blockId)) {
        return prev;
      }
      return [...prev, ctx];
    });
  }, []);

  /** Keep `chatBlockContexts` aligned with the mention snapshot on send (see WebDashboardChatSidebar handleSubmit). */
  const handleBlockContextsOnSubmit = useCallback(
    (contexts: Array<{ blockId: string; blockTitle: string; blockType: string }>) => {
      setSubmittedBlockContexts(contexts);
      setChatBlockContexts(
        contexts.map(c => ({
          blockId: c.blockId,
          blockTitle: c.blockTitle,
          blockType: c.blockType,
        }))
      );
    },
    []
  );

  // Clear frozen block contexts when AI finishes thinking so borders reset
  const prevIsAiThinkingRef = useRef(false);
  useEffect(() => {
    if (prevIsAiThinkingRef.current && !isAiThinking) {
      setSubmittedBlockContexts([]);
      setChatBlockContexts([]);
    }
    prevIsAiThinkingRef.current = isAiThinking;
  }, [isAiThinking]);

  // Same-turn block screenshot capture (html2canvas in iframe)
  const [blockScreenshotRequest, setBlockScreenshotRequest] = useState<{
    requestId: string;
    blockIds: string[];
  } | null>(null);
  const blockScreenshotRequestRef = useRef(blockScreenshotRequest);
  const capturedBlockIdsRef = useRef<Set<string>>(new Set());
  const captureInFlightRef = useRef<Set<string>>(new Set());
  const captureRetriesRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    blockScreenshotRequestRef.current = blockScreenshotRequest;
  }, [blockScreenshotRequest]);

  // User can only edit if they have permission AND are in editor mode
  const isEditingEnabled = canEditDashboard && dashboardMode === "editor" && !isDashboardCreateMode;

  // Prefetch agent threads after the dashboard loads so the chat sidebar can
  // open instantly without waiting for a getAgentThreads round-trip.
  usePrefetchAgentThreads(
    isSQLiteInitializedFromHook && !hasSuperAdminAccess
      ? ((isEditingEnabled ? "web_dashboard_editor" : "web_dashboard_viewer") as AgentType)
      : undefined,
    dashboardData?.spreadsheet_id,
    dashboardId
  );

  // While an AI edit is in-flight from the chat sidebar, show a glowing rim around the blocks
  // that are expected to be updated. If the user didn't select specific blocks, fall back to
  // block IDs inferred from the agent's update_web_dashboard tool calls.
  const refreshingBlockIds = useMemo((): Set<string> => {
    if (!(dashboardMode === "editor" && isAiThinking)) {
      return new Set();
    }

    if (submittedBlockContexts.length > 0) {
      return new Set(submittedBlockContexts.map(ctx => ctx.blockId));
    }

    return agentInferredRefreshingBlockIds;
  }, [dashboardMode, isAiThinking, submittedBlockContexts, agentInferredRefreshingBlockIds]);
  // Preview a theme change locally (drives the live iframe re-theme) WITHOUT persisting. The
  // selector fires this per palette/toggle click, so browsing palettes costs zero S3 writes /
  // version rows / undo entries — it persists ONCE via handleThemeChange when it closes dirty.
  const previewTheme = useCallback(
    (next: { themeId?: string; mode?: string; background?: string }) => {
      if (next.themeId) {
        setThemeId(next.themeId);
      }
      if (next.mode) {
        setThemeMode(next.mode as ThemeMode);
      }
      if (next.background) {
        setThemeBackground(next.background as ThemeBackground);
      }
    },
    []
  );

  // Latest handleThemeChange for closures declared before it exists (the chat theme hook, toast
  // actions); assigned right after handleThemeChange's declaration below.
  const handleThemeChangeRef = useRef<
    (next: { themeId?: string; mode?: string; background?: string }) => void
  >(() => undefined);

  const openChatSidebar = useCallback(() => setIsChatSidebarOpen(true), []);
  // The chat-driven custom-theme surface: request form, editor-thread handoff, Edit prefill,
  // and the result card's preview / apply / keep-current flow.
  const {
    isCustomThemeAgentEnabled,
    chatPrefillMessage,
    chatPrefillContext,
    prefillComposer,
    handlePrefillConsumed,
    pendingThemeChatMessage,
    handleAutoSubmitConsumed,
    isThemeRequestOpen,
    themeRequestInstanceId,
    themeRequestFocusNonce,
    handleEditTheme,
    handleThemeFormRequested,
    handleRequestTheme,
    handleThemeRequestSubmitted,
    handleThemeRequestDismissed,
    chatThemeSpecNonce,
    handleChatThemePreview,
    handleChatThemeCancelPreview,
    handleChatThemeApply,
  } = useChatThemeControls({
    dashboardId,
    openChatSidebar,
    liveTheme: { themeId, mode: themeMode, background: themeBackground },
    previewTheme,
    persistThemeRef: handleThemeChangeRef,
  });

  // Pending messages (e.g., from "Explain" clicks) to be submitted with next user message
  const [pendingMessages, setPendingMessages] = useState<
    {
      id: string;
      role: "user" | "agent";
      content: string;
      autoSubmit?: boolean;
      instant?: boolean;
      backendContent?: string;
      meta?: {
        auto_fix?: {
          is_auto_fix: true;
          attempt_index?: number;
          max_attempts?: number;
          error_message?: string;
          source?: "fix_button" | "auto_fix_retry";
        };
      };
    }[]
  >([]);

  const oauthProvider = useMemo<"google" | "microsoft">(
    () => ((dashboardData as any)?.source_type === "excel" ? "microsoft" : "google"),
    [dashboardData]
  );

  const handleChatNeedsOAuth = useCallback(
    (err: NeedsGoogleOAuthError) => {
      sessionStorage.setItem(
        "web_dashboard_pending_chat_message",
        JSON.stringify({
          message: err.messageToSend,
          prior_messages: err.priorMessages ?? null,
          meta: err.meta ?? null,
          chat_mode: err.chatMode,
          thread_id: err.threadId ?? null,
        })
      );
      setIsChatOAuthRequired(true);
      setIsChatSidebarOpen(true);
      if (oauthProvider === "microsoft") {
        setIsMicrosoftAuthModalOpen(true);
      } else {
        setIsGoogleSheetsAuthModalOpen(true);
      }
    },
    [oauthProvider]
  );
  const [sidebarNavItems, setSidebarNavItems] = useState<
    Array<{ id: string; label: string; element: HTMLElement }>
  >([]);
  const [refreshRunId, setRefreshRunId] = useState<string | null>(null);
  const [refreshStartTime, setRefreshStartTime] = useState<number | null>(null);
  const { data: latestRun } = useLatestDashboardRun(dashboardId, {
    enabled: Boolean(dashboardId) && !isDashboardCreateMode,
  });
  const hasAttemptedReattachRef = useRef(false);

  // Mount-time reattach: if the server reports an in-flight manual refresh that started
  // less than MANUAL_REFRESH_TIMEOUT_MS ago, resume polling under the same run_id so a
  // page reload mid-refresh doesn't drop the user's progress indicator.
  useEffect(() => {
    if (hasAttemptedReattachRef.current) {
      return;
    }
    if (!latestRun || refreshRunId) {
      return;
    }

    const status = latestRun.status;
    const isInFlight = status === "queued" || status === "running";
    const isManual = latestRun.refresh_type === "manual";
    const startedMs = latestRun.started_dt ? Date.parse(latestRun.started_dt) : 0;
    const isRecent = startedMs > 0 && Date.now() - startedMs < MANUAL_REFRESH_TIMEOUT_MS;

    if (isInFlight && isManual && isRecent) {
      hasAttemptedReattachRef.current = true;
      setRefreshRunId(latestRun.run_id);
      setRefreshStartTime(startedMs);
    }
  }, [latestRun, refreshRunId]);

  const [largeChangeConfirmation, setLargeChangeConfirmation] = useState<{
    tabsConfig?: WebDashboardTabsConfig;
    excelUploadS3Key?: string;
    spreadsheetName?: string;
    excelUploadClientMetrics?: {
      file_size_bytes: number;
      upload_type: "post" | "multipart";
      upload_duration_ms: number;
      multipart_part_count?: number;
    };
    ratio: number;
    changedCount: number;
    totalCount: number;
  } | null>(null);
  const [isConfirmingLargeChange, setIsConfirmingLargeChange] = useState(false);

  const [undoRefreshSnapshotS3Path, setUndoRefreshSnapshotS3Path] = useState<string | null>(null);
  const [isUndoingRefresh, setIsUndoingRefresh] = useState(false);

  // TODO(tommy): Right now, the cleanup function returned by the createModalDataTable helper
  // function (which is called by the LLM-generated JS code) does not get called when the
  // bottom drawer is closed.
  const [bottomDrawerContent, setBottomDrawerContent] = useState<HTMLElement[] | null>(null);

  // State for undo functionality - stores a stack of previous dashboard configs
  // Each entry has a unique id so error handlers can remove their specific entry
  const [undoStack, setUndoStack] = useState<
    Array<{
      id: string;
      contentConfig: WebDashboardContentConfig;
      spreadsheetVersionId?: number;
    }>
  >([]);

  // Map chat message IDs to their corresponding undo entry IDs
  // Used to enable per-message revert in the chat sidebar
  const [messageToUndoEntry, setMessageToUndoEntry] = useState<Map<string, string>>(new Map());

  const bottomDrawerContentContainerRef = useRef<HTMLDivElement | null>(null);
  const bottomDrawerRef = useRef<HTMLDivElement | null>(null);

  // Track per-block render completion to fire VibeDashLoaded once all data blocks are painted.
  // Data blocks are metric_card, chart, and data_table — the types that run async render functions.
  // Blocks that fail to render also call onRendered so a single bad block doesn't block the event.
  const renderedBlockIdsRef = useRef<Set<string>>(new Set());
  const hasTrackedLoadedRef = useRef(false);

  // Latches true if this viewer session ever started in the build/create flow.
  // mountTimeRef is anchored at mount (build start), so firing VibeDashLoaded /
  // VibeDashViewed for such a session would inflate duration_ms with the entire
  // planning + build time. The build completing flips isDashboardCreateMode to false
  // before the final block render, so the live value alone can't suppress the event.
  const startedInBuildRef = useRef(false);

  // Tracks the dashboardId the view-tracking refs above currently correspond to,
  // so navigation resets them exactly once per dashboard (see the effect below).
  const trackedDashboardIdRef = useRef<string | undefined>(undefined);

  // End-to-end `web_dashboard.full_load` transaction (navigation start → all blocks
  // painted, abandoned loads emitted as `cancelled`) — see the hook for the full story.
  // Build-flow sessions are suppressed for the same reason as VibeDashLoaded: the
  // duration would include planning + build time, not load time.
  const fullLoad = useFullLoadTransaction({
    dashboardId,
    isSuppressed: () => startedInBuildRef.current,
    getDbLoadMs: () => dbLoadMsRef.current,
  });

  // Marks the end-to-end "fully loaded" moment (all blocks painted): completes the
  // full_load transaction and drops a breadcrumb anchoring the client-side timeline
  // in replays alongside the per-phase markers. Fires under the same non-build guard
  // as the VibeDashLoaded analytics event so build time doesn't inflate the duration.
  const emitFullyLoadedPerf = useCallback(
    (numDataBlocks: number) => {
      fullLoad.markComplete(numDataBlocks);
      addPerfBreadcrumb("fully_loaded", {
        dashboard_id: dashboardId,
        duration_ms: Date.now() - mountTimeRef.current,
        db_load_ms: dbLoadMsRef.current ?? undefined,
        num_data_blocks: numDataBlocks,
      });
    },
    [dashboardId, fullLoad]
  );

  const handleBlockRendered = useCallback(
    (blockId: string) => {
      if (hasTrackedLoadedRef.current) {
        return;
      }

      renderedBlockIdsRef.current.add(blockId);

      const contentSpec = dashboardData?.content_config?.content_spec as
        | WebDashboardContentSpec
        | undefined;
      const dataBlockIds = getDashboardDataBlockIds(contentSpec);

      if (
        !isDashboardCreateMode &&
        !startedInBuildRef.current &&
        dataBlockIds.length > 0 &&
        dataBlockIds.every(id => renderedBlockIdsRef.current.has(id))
      ) {
        hasTrackedLoadedRef.current = true;
        trackDashboardEvent(AnalyticsEventNameType.VibeDashLoaded, {
          vibe_dash_id: dashboardId,
          ss_id: dashboardData?.spreadsheet_id,
          vibe_dash_name: dashboardData?.content_config?.title,
          vibe_dash_slicers: slicerNames,
          vibe_dash_db_load_ms: dbLoadMsRef.current ?? undefined,
          duration_ms: Date.now() - mountTimeRef.current,
        });
        emitFullyLoadedPerf(dataBlockIds.length);
      }

      if (
        shouldShowDashboardBuildConfettiAfterRenderRef.current &&
        dataBlockIds.length > 0 &&
        dataBlockIds.every(id => renderedBlockIdsRef.current.has(id))
      ) {
        shouldShowDashboardBuildConfettiAfterRenderRef.current = false;
        setShowDashboardBuildConfetti(true);
      }
    },
    [
      dashboardData,
      dashboardId,
      slicerNames,
      trackDashboardEvent,
      isDashboardCreateMode,
      emitFullyLoadedPerf,
    ]
  );

  // For dashboards with no async data blocks, no block will call handleBlockRendered,
  // so fire VibeDashLoaded once the SQLite DB is ready.
  useEffect(() => {
    if (
      !isSQLiteInitializedFromHook ||
      hasTrackedLoadedRef.current ||
      isDashboardCreateMode ||
      startedInBuildRef.current
    ) {
      return;
    }
    const contentSpec = dashboardData?.content_config?.content_spec as
      | WebDashboardContentSpec
      | undefined;

    if (!contentSpec) {
      return;
    }

    const hasDataBlocks = getDashboardDataBlockIds(contentSpec).length > 0;

    if (!hasDataBlocks) {
      hasTrackedLoadedRef.current = true;
      trackDashboardEvent(AnalyticsEventNameType.VibeDashLoaded, {
        vibe_dash_id: dashboardId,
        ss_id: dashboardData?.spreadsheet_id,
        vibe_dash_name: dashboardData?.content_config?.title,
        vibe_dash_slicers: slicerNames,
        vibe_dash_db_load_ms: dbLoadMsRef.current ?? undefined,
        duration_ms: Date.now() - mountTimeRef.current,
      });
      emitFullyLoadedPerf(0);
    }
  }, [
    isSQLiteInitializedFromHook,
    dashboardData,
    dashboardId,
    slicerNames,
    trackDashboardEvent,
    isDashboardCreateMode,
    emitFullyLoadedPerf,
  ]);

  const refreshDashboardMutation = useRefreshDashboardMutation(dashboardId);
  const saveDashboardConfigMutation = useSaveDashboardConfigMutation(dashboardId ?? "");

  const superAdminDashboardVersions = useSuperAdminDashboardVersions({
    dashboardId,
    queryClient,
    setToastProps,
  });

  const { data: runStatus } = useDashboardRunStatus(dashboardId, refreshRunId, {
    refetchInterval: data => {
      // Stop polling if completed or failed
      if (data?.status === "completed" || data?.status === "failed") {
        return false;
      }
      // Poll every 2 seconds
      return 2000;
    },
  });

  const isRefreshing = Boolean(
    refreshRunId && runStatus?.status !== "completed" && runStatus?.status !== "failed"
  );

  const [isCheckingChanges, setIsCheckingChanges] = useState(false);

  // After a refresh completes we reload the page; persist an undo snapshot in localStorage
  // so we can show an "Undo refresh" button on the new page load.
  useEffect(() => {
    if (!dashboardId) {
      return;
    }
    const key = `web_dashboard_refresh_undo:${dashboardId}`;
    const raw = localStorage.getItem(key);
    if (!raw) {
      setUndoRefreshSnapshotS3Path(null);
      return;
    }
    try {
      const parsed = JSON.parse(raw) as {
        undo_web_dashboard_json_s3_path?: string;
        saved_at_ms?: number;
      };
      const savedAt = parsed.saved_at_ms ?? 0;
      const isFresh = Date.now() - savedAt < 30 * 60 * 1000; // 30 minutes
      if (!parsed.undo_web_dashboard_json_s3_path || !isFresh) {
        localStorage.removeItem(key);
        setUndoRefreshSnapshotS3Path(null);
        return;
      }
      setUndoRefreshSnapshotS3Path(parsed.undo_web_dashboard_json_s3_path);
    } catch {
      localStorage.removeItem(key);
      setUndoRefreshSnapshotS3Path(null);
    }
  }, [dashboardId]);

  // Use only the saved plan_overview (with optional fallback to description) as the AI-style summary.
  const rawPlan = (dashboardData?.content_config?.plan as any) ?? undefined;
  const planOverview =
    (rawPlan?.plan_overview as string | undefined) ??
    dashboardData?.content_config?.description ??
    "";

  // Auto-open the "Edit" chat sidebar when the dashboard is opened
  // with a ?show_editor=1 query parameter (for users who can edit). The
  // parameter is removed from the URL after being handled so that the URL
  // in the address bar remains clean and shareable.
  const hasAutoOpenedChatRef = useRef(false);

  useEffect(() => {
    if (hasAutoOpenedChatRef.current) {
      return;
    }
    if (!dashboardData || loading || error) {
      return;
    }
    if (shouldShowDashboardBuildLoader) {
      return;
    }
    if (!dashboardId) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const showEditor = searchParams.get("show_editor");

    if (!showEditor) {
      return;
    }

    // Mark that we've handled the query param for this navigation so we
    // don't try to auto-open again.
    hasAutoOpenedChatRef.current = true;

    // Remove the show_editor param from the URL without triggering a reload,
    // so the address bar stays clean and shareable.
    searchParams.delete("show_editor");
    const newSearch = searchParams.toString();
    history.replace({
      ...location,
      search: newSearch ? `?${newSearch}` : "",
    });

    // Only switch to editor mode and open the chat sidebar if the current
    // user actually has edit permissions for this dashboard.
    if (canEditDashboard) {
      setDashboardMode("editor");
      setIsChatSidebarOpen(true);
    }
  }, [
    dashboardData,
    loading,
    error,
    shouldShowDashboardBuildLoader,
    canEditDashboard,
    dashboardId,
    history,
    location,
  ]);

  // Handle run status changes
  useEffect(() => {
    if (!runStatus || !refreshRunId) {
      return;
    }

    if (runStatus.status === "completed") {
      // Track refresh completed
      track(AnalyticsEventNameType.VibeDashRefreshCompleted, {
        vibe_dash_id: dashboardId,
        ss_id: dashboardData?.spreadsheet_id,
        vibe_dash_name: dashboardData?.content_config?.title,
        vibe_dash_slicers: slicerNames,
        duration_ms: refreshStartTime ? Date.now() - refreshStartTime : undefined,
      });

      // Always clear any prior undo banner state. We'll re-set it below if the new run
      // provides a fresh undo snapshot.
      try {
        localStorage.removeItem(`web_dashboard_refresh_undo:${dashboardId}`);
      } catch {
        // ignore localStorage errors
      }

      if (runStatus.undo_web_dashboard_json_s3_path) {
        try {
          localStorage.setItem(
            `web_dashboard_refresh_undo:${dashboardId}`,
            JSON.stringify({
              undo_web_dashboard_json_s3_path: runStatus.undo_web_dashboard_json_s3_path,
              saved_at_ms: Date.now(),
            })
          );
        } catch (e) {
          // ignore localStorage errors
        }
      } else {
        // Set flag in localStorage to show success notification after reload.
        // If an undo snapshot is present, we show a persistent "Undo refresh"
        // banner instead of the transient toast.
        localStorage.setItem("dashboard_refresh_success", "true");
      }
      // Reload the page to show updated dashboard
      window.location.reload();
    } else if (runStatus.status === "failed") {
      // Track refresh failed (polling detected failure)
      track(AnalyticsEventNameType.VibeDashRefreshFailed, {
        vibe_dash_id: dashboardId,
        ss_id: dashboardData?.spreadsheet_id,
        vibe_dash_name: dashboardData?.content_config?.title,
        vibe_dash_slicers: slicerNames,
        duration_ms: refreshStartTime ? Date.now() - refreshStartTime : undefined,
        error_message: runStatus.error_message || "Unknown error",
      });

      setRefreshRunId(null);
      setRefreshStartTime(null);

      // A concurrent edit aborted the refresh (the edit won). Writer-neutral copy: the
      // most common cause is the same user editing in another tab. Same red toast as the
      // save-conflict path — never a native alert(), and never the raw error_message
      // (developer-speak like "Refresh aborted: Dashboard was modified (manual or AI edit)…").
      if (isConcurrentEditRefreshAbort(runStatus)) {
        setToastProps({
          message:
            "This dashboard was updated while it was refreshing so the refresh was canceled.",
          type: "error",
          position: "bottom",
          disableAutoClose: true,
          action: {
            label: "Try again",
            onClick: () => void handleRefreshDashboardRef.current(),
          },
          onClose: () => setToastProps(null),
        });
      } else {
        setToastProps({
          message: `Dashboard refresh failed: ${runStatus.error_message || "Unknown error"}`,
          type: "error",
          position: "bottom",
          disableAutoClose: true,
          onClose: () => setToastProps(null),
        });
      }
    }
  }, [runStatus, refreshRunId, dashboardId, dashboardData, slicerNames, refreshStartTime, track]);

  // Set initial dashboard mode based on edit permissions
  useEffect(() => {
    if (!isLoadingPermissions) {
      setDashboardMode(canEditDashboard && !isDashboardCreateMode ? "editor" : "viewer");
    }
  }, [canEditDashboard, isDashboardCreateMode, isLoadingPermissions]);

  // Reset view tracking when navigating to a different dashboard, then latch the
  // build-flow flag while the current dashboard is building. Combined into one effect
  // (rather than a reset effect + a latch effect keyed only on isDashboardCreateMode)
  // so navigating between two dashboards that are both still building — where
  // isDashboardCreateMode never changes value — still re-evaluates the latch. The
  // latch is only ever set true within a dashboard, so build completion on the same
  // dashboard (isDashboardCreateMode flips to false) does not clear it.
  useEffect(() => {
    if (trackedDashboardIdRef.current !== dashboardId) {
      const isSwitch = trackedDashboardIdRef.current !== undefined;
      if (isSwitch) {
        // Navigating away from a dashboard whose load never completed is an
        // abandoned load. Emit before resetting startedInBuildRef below so the
        // *previous* dashboard's build-suppression still applies.
        fullLoad.abandonAndReset(dashboardId);
        // Re-arm load tracking for the new dashboard. renderedBlockIdsRef /
        // hasTrackedLoadedRef / mountTimeRef were previously never reset, so a
        // dashboard reached via SPA navigation could not fire VibeDashLoaded at
        // all (and would have reported an inflated duration if it did) — resetting
        // fixes that and lets full_load complete once per dashboard visit.
        renderedBlockIdsRef.current = new Set();
        hasTrackedLoadedRef.current = false;
        mountTimeRef.current = Date.now();
      }
      trackedDashboardIdRef.current = dashboardId;
      hasTrackedViewRef.current = false;
      hasRecordedViewRef.current = false;
      startedInBuildRef.current = false;
    }
    if (isDashboardCreateMode) {
      startedInBuildRef.current = true;
    }
  }, [dashboardId, isDashboardCreateMode, fullLoad]);

  // Record dashboard view in the backend (once per dashboard load, non-super-admin).
  // Authenticated viewers hit the authenticated endpoint; anonymous viewers of a
  // public dashboard hit the unauthenticated variant keyed on their sticky
  // localStorage id (the same temp_user_key used for the vibe_dash_viewed event),
  // so their views land in web_dashboard_view too. Super-admin views stay excluded.
  useEffect(() => {
    if (
      !dashboardData ||
      !dashboardId ||
      isLoadingPermissions ||
      hasSuperAdminAccess ||
      hasRecordedViewRef.current
    ) {
      return;
    }
    hasRecordedViewRef.current = true;
    const recordView = isAuthenticated
      ? apiClient.recordWebDashboardView({ web_dashboard_id: dashboardId })
      : apiClient.recordWebDashboardViewPublic({
          web_dashboard_id: dashboardId,
          record_web_dashboard_view_public_request: { viewer_key: getAnonymousUserId() },
        });
    recordView.catch(err => {
      Sentry.captureException(err, { extra: { dashboardId } });
    });
    // Only re-run when the dashboard or auth context changes, not on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId, isAuthenticated, hasSuperAdminAccess, isLoadingPermissions, !!dashboardData]);

  // Track dashboard view (only once per dashboard)
  // Wait for permissions to load to ensure accurate viewer type tracking
  // Uses trackDashboardEvent to support both authenticated and anonymous users
  useEffect(() => {
    if (
      dashboardData &&
      dashboardId &&
      !isLoadingPermissions &&
      !hasTrackedViewRef.current &&
      !hasSuperAdminAccess &&
      !isDashboardCreateMode &&
      !startedInBuildRef.current
    ) {
      hasTrackedViewRef.current = true;
      trackDashboardEvent(AnalyticsEventNameType.VibeDashViewed, {
        vibe_dash_id: dashboardId,
        ss_id: dashboardData.spreadsheet_id,
        vibe_dash_name: dashboardData.content_config?.title,
        vibe_dash_viewer_type: canEditDashboard
          ? VibeDashViewerType.Owner
          : VibeDashViewerType.Viewer,
        vibe_dash_slicers: slicerNames,
      });
    }
  }, [
    dashboardData,
    dashboardId,
    trackDashboardEvent,
    canEditDashboard,
    hasSuperAdminAccess,
    isLoadingPermissions,
    slicerNames,
    isDashboardCreateMode,
  ]);

  // Enable transitions after a short delay to prevent transitions from firing on page load.
  useEffect(() => {
    if (iframeBody) {
      setTimeout(() => {
        iframeBody.classList.remove("disable-transitions");
      }, 100);
    }
  }, [iframeBody]);

  // Apply theme when themeId / mode / background changes or when iframe becomes available.
  // While the build loader is up, the page background is pinned to the loader's own surface
  // color — the nav is transparent, so the theme's tinted page background would otherwise show
  // through it as a colored banner over the gray loader. The theme's colors land together with
  // the first rendered content.
  useEffect(() => {
    if (!iframeWindow) {
      return;
    }
    const resolved = resolveTheme(themeId, themeMode, themeBackground);

    const theme = shouldShowDashboardBuildLoader
      ? {
          ...resolved,
          tailwind: {
            ...resolved.tailwind,
            general: {
              ...resolved.tailwind.general,
              pageBackground: BUILD_LOADER_PAGE_BACKGROUND,
            },
            nav: {
              ...resolved.tailwind.nav,
              backgroundColor: BUILD_LOADER_PAGE_BACKGROUND,
              borderColor: "transparent",
              shadow: "none",
            },
          },
        }
      : resolved;

    // Store theme on window for use by render functions (createChart, createDataTable)
    iframeWindow.__coeffWebDashboardTheme = theme;

    // Apply Tailwind theme vars to the main app document too (used by some modals/snackbars
    // rendered above the iframe, e.g. Uploading snackbar above the AntD modal mask). Same
    // semantic-token choice as the iframe path: TEXT tokens, not the chart fills.
    applyTailwindTheme(window.document, theme.tailwind, theme.semanticText ?? theme.chartjs);

    // Apply themes to Tailwind, Chart.js and AG Grid themes
    applyTheme(iframeWindow, theme);
    // `resolvedTheme` is a dep because a custom theme edited in place keeps its id: re-registering
    // invalidates the cache, but without this we'd never re-resolve. React Query's structural
    // sharing keeps the reference stable when a refetch returns an identical spec, so this does
    // not re-run on every poll. `chatThemeSpecNonce` covers the same-id case driven from chat,
    // where nothing in the trio changes (see handleChatThemePreview).
  }, [
    themeId,
    themeMode,
    themeBackground,
    shouldShowDashboardBuildLoader,
    iframeWindow,
    resolvedTheme,
    chatThemeSpecNonce,
  ]);

  // Hydrate themeId / mode / background from the persisted config once per dashboard load.
  // This must NOT re-sync on every content_config change: rapid theme switches fire concurrent
  // saves whose onSuccess invalidates this query, and an earlier save's refetch can resolve after
  // a newer optimistic pick — re-syncing would then clobber the newer theme with the stale one,
  // producing a visible revert/flicker (theme3 → theme2 → theme3). Local state + the optimistic
  // cache update in handleThemeChange are the source of truth after hydration.
  const hydratedThemeDashboardIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const savedConfig = dashboardData?.content_config as
      | { theme_id?: string; mode?: ThemeMode; background?: ThemeBackground }
      | undefined;
    const savedThemeId = savedConfig?.theme_id;
    if (!savedThemeId || hydratedThemeDashboardIdRef.current === dashboardId) {
      return;
    }
    if (savedThemeId !== themeId) {
      setThemeId(savedThemeId);
    }
    // Legacy configs persist no mode/background: seed from the legacy→palette map (a dark legacy
    // theme must open the selector with Dark ON, or the first palette pick commits light mode).
    const legacySeed = LEGACY_THEME_TO_PALETTE[savedThemeId];
    const savedMode = savedConfig?.mode ?? legacySeed?.mode;
    const savedBackground = savedConfig?.background ?? legacySeed?.background;
    if (savedMode && savedMode !== themeMode) {
      setThemeMode(savedMode);
    }
    if (savedBackground && savedBackground !== themeBackground) {
      setThemeBackground(savedBackground);
    }
    hydratedThemeDashboardIdRef.current = dashboardId;
  }, [dashboardData?.content_config, dashboardId, themeId, themeMode, themeBackground]);

  // A save hit a 409 edit conflict (something changed the dashboard since we read it).
  // The caller has already rolled back its optimistic update; offer a reload instead of
  // the generic failure toast. Writer-neutral copy: the most common conflict is the same
  // user editing in another tab, so we don't name who made the intervening change.
  const showSaveConflictToast = useCallback(() => {
    setToastProps({
      message:
        "This dashboard was updated while you were editing so your changes could not be saved.",
      type: "error",
      position: "bottom",
      // The reload prompt is the only way to recover — don't auto-dismiss it.
      disableAutoClose: true,
      action: {
        label: "Reload",
        onClick: () =>
          queryClient.invalidateQueries({
            queryKey: [QueryCacheKey.WEB_DASHBOARD, dashboardId],
            exact: false,
          }),
      },
      onClose: () => setToastProps(null),
    });
  }, [dashboardId, queryClient]);

  // Handle theme change - update local state and persist to backend
  const handleThemeChange = useCallback(
    (next: { themeId?: string; mode?: string; background?: string }) => {
      // Merge the requested change with current state so mode/background are never dropped
      const newThemeId = next.themeId ?? themeId;
      const newMode: ThemeMode = (next.mode as ThemeMode | undefined) ?? themeMode;
      const newBackground: ThemeBackground =
        (next.background as ThemeBackground | undefined) ?? themeBackground;

      if (!dashboardId) {
        setThemeId(newThemeId);
        setThemeMode(newMode);
        setThemeBackground(newBackground);
        return;
      }

      // Get current dashboard data for undo stack
      const currentDashboardData = queryClient.getQueryData([
        QueryCacheKey.WEB_DASHBOARD,
        dashboardId,
      ]) as GetWebDashboard200Response;

      if (!currentDashboardData?.content_config) {
        setThemeId(newThemeId);
        setThemeMode(newMode);
        setThemeBackground(newBackground);
        return;
      }

      // No-op against the PERSISTED config, not local state — after a preview session the local
      // state already holds the values being committed, but the backend doesn't yet. (A pure
      // mode/background flip must still persist; legacy configs may lack all three fields.)
      const savedConfig = currentDashboardData.content_config as {
        theme_id?: string;
        mode?: ThemeMode;
        background?: ThemeBackground;
      };
      // Legacy configs omit mode/background: resolve their effective values via the
      // legacy→palette seed (same as hydration) so the no-op check and the error rollback don't
      // treat a dark legacy theme as light.
      const savedThemeId = savedConfig.theme_id ?? DEFAULT_THEME_ID;
      const savedSeed = LEGACY_THEME_TO_PALETTE[savedThemeId];
      const savedMode = savedConfig.mode ?? savedSeed?.mode ?? DEFAULT_THEME_MODE;
      const savedBackground =
        savedConfig.background ?? savedSeed?.background ?? DEFAULT_THEME_BACKGROUND;
      if (
        newThemeId === savedThemeId &&
        newMode === savedMode &&
        newBackground === savedBackground
      ) {
        // Nothing to persist; still sync local state (e.g. toast Reset back to the saved theme).
        setThemeId(newThemeId);
        setThemeMode(newMode);
        setThemeBackground(newBackground);
        return;
      }

      // Push current state to undo stack before making changes
      const undoEntryId = crypto.randomUUID();
      setUndoStack(prev => [
        ...prev,
        {
          id: undoEntryId,
          contentConfig: currentDashboardData.content_config!,
          spreadsheetVersionId: currentDashboardData.spreadsheet_version_id,
        },
      ]);

      // Update local theme state
      setThemeId(newThemeId);
      setThemeMode(newMode);
      setThemeBackground(newBackground);

      // Track theme change. Palette ids resolve their name from BUILTIN_PALETTES; legacy slugs
      // from the authored theme list; registered custom themes via resolveTheme's themeName.
      const newThemeName =
        BUILTIN_PALETTES.find(p => p.id === newThemeId)?.name ??
        themes.find(t => t.themeId === newThemeId)?.themeName ??
        resolveTheme(newThemeId, newMode, newBackground).themeName;
      const savedThemeName =
        BUILTIN_PALETTES.find(p => p.id === savedThemeId)?.name ??
        themes.find(t => t.themeId === savedThemeId)?.themeName ??
        resolveTheme(savedThemeId, savedMode, savedBackground).themeName;
      track(AnalyticsEventNameType.VibeDashThemeChanged, {
        vibe_dash_id: dashboardId,
        ss_id: currentDashboardData.spreadsheet_id,
        vibe_dash_name: currentDashboardData.content_config?.title,
        vibe_dash_theme_id: newThemeId,
        vibe_dash_theme_name: newThemeName,
        vibe_dash_previous_theme_id: savedThemeId,
        vibe_dash_previous_theme_name: savedThemeName,
        vibe_dash_slicers: slicerNames,
      });

      const newContentConfig = {
        ...currentDashboardData.content_config,
        theme_id: newThemeId,
        mode: newMode,
        background: newBackground,
      };

      // Update local cache
      queryClient.setQueryData([QueryCacheKey.WEB_DASHBOARD, dashboardId], {
        ...currentDashboardData,
        content_config: newContentConfig,
      });

      // Persist to backend (excluding derived fields)
      const contentConfigToSave = {
        ...newContentConfig,
        content_html: undefined,
        js_code: undefined,
      };

      saveDashboardConfigMutation.mutate(
        {
          content_config: contentConfigToSave,
          spreadsheet_version_id: currentDashboardData.spreadsheet_version_id,
          // Theme picks are pure config-field changes — declared as a delta so a stale
          // token rebases over disjoint concurrent edits instead of 409ing.
          edit_delta: buildEditDelta(currentDashboardData.content_config, newContentConfig),
        },
        {
          onSuccess: data => {
            if (data.merged_concurrent_changes) {
              // Rebased onto content this tab has never seen — stale undo snapshots would
              // revert other writers, so drop them.
              setUndoStack([]);
              setMessageToUndoEntry(new Map());
            }
          },
          onError: error => {
            // Revert the optimistic updates to the last PERSISTED values (local state may hold
            // previewed values that were never saved) to restore consistency with the backend.
            setThemeId(savedThemeId);
            setThemeMode(savedMode);
            setThemeBackground(savedBackground);
            queryClient.setQueryData(
              [QueryCacheKey.WEB_DASHBOARD, dashboardId],
              currentDashboardData
            );

            if (isSaveConflictError(error)) {
              showSaveConflictToast();
              // A newer version exists — every undo snapshot in this tab is stale.
              setUndoStack([]);
              setMessageToUndoEntry(new Map());
            } else {
              // Remove this specific entry from the undo stack since save failed
              setUndoStack(prev => prev.filter(entry => entry.id !== undoEntryId));
            }
          },
        }
      );
    },
    [
      dashboardId,
      queryClient,
      saveDashboardConfigMutation,
      themeId,
      themeMode,
      themeBackground,
      track,
      slicerNames,
      showSaveConflictToast,
    ]
  );

  handleThemeChangeRef.current = handleThemeChange;

  // A theme change diverged from what the selector opened with (per click while browsing, and again
  // when a dirty session closes and persists): confirm with a toast whose Reset action restores that
  // opening state. Held longer than a standard toast (it is the only way back to the old theme), at
  // the same 5s the block-delete undo toast uses.
  const handleThemeCommitted = useCallback(
    (revertTo: { themeId: string; mode: string; background: string }) => {
      setToastProps({
        message: THEME_TOAST_MESSAGE,
        type: "info",
        materialIcon: "info",
        duration: 5000,
        position: "bottom",
        action: {
          label: "Reset",
          onClick: () => handleThemeChangeRef.current(revertTo),
        },
        onClose: () => setToastProps(null),
      });
    },
    []
  );

  // The browse came back to where it started, so the toast's Reset would target the current theme.
  const handleThemeUncommitted = useCallback(() => {
    setToastProps(prev => (prev?.message === THEME_TOAST_MESSAGE ? null : prev));
  }, []);

  // Custom theme deleted from the selector (no confirmation step): offer Undo, which restores
  // the soft-deleted row and refreshes the selector list.
  const restoreCustomTheme = useRestoreWebDashboardCustomTheme();
  const restoreCustomThemeRef = useRef(restoreCustomTheme);
  restoreCustomThemeRef.current = restoreCustomTheme;
  const handleThemeDeleted = useCallback((deleted: { themeId: string; name: string }) => {
    setToastProps({
      message: `Deleted ${deleted.name}`,
      type: "info",
      materialIcon: "delete",
      duration: 5000,
      position: "bottom",
      action: {
        label: "Undo",
        onClick: () => restoreCustomThemeRef.current.mutate(deleted.themeId),
      },
      onClose: () => setToastProps(null),
    });
  }, []);

  const handleThemeDeleteFailed = useCallback(() => {
    setToastProps({
      message: "Failed to delete theme",
      type: "error",
      materialIcon: "error",
      onClose: () => setToastProps(null),
    });
  }, []);

  const mainContentRef = useRef<HTMLElement>(null);
  const dashboardTitleRef = useRef<HTMLHeadingElement>(null);
  const [isHeaderEditOpen, setIsHeaderEditOpen] = useState(false);

  // Disable scrolling on iframe content body when any modal is open
  const isSomeModalOpen = Boolean(bottomDrawerContent != null);
  useEffect(() => {
    if (!iframeWindow) {
      return;
    }
    if (isSomeModalOpen) {
      document.documentElement.style.overscrollBehavior = "none";
      iframeWindow.document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overscrollBehavior = "";
      iframeWindow.document.documentElement.style.overflow = "auto";
    }

    return () => {
      document.documentElement.style.overscrollBehavior = "";
    };
  }, [iframeWindow, isSomeModalOpen]);

  // Swipe-down-to-close for the bottom drawer on mobile. Gating `enabled` on the drawer being open
  // means the hook's cleanup (which clears the gesture's inline transform) runs exactly when the
  // drawer closes by ANY path (swipe, X, Escape, backdrop) — flash-free because by then the class
  // is already `translate-y-full invisible`.
  useSwipeDownToClose({
    sheetRef: bottomDrawerRef,
    enabled: isMobileViewport && bottomDrawerContent != null,
    onDismiss: () => setBottomDrawerContent(null),
  });

  // Handle escape key press for closing open modals: bottom drawer
  useEffect(() => {
    if (!iframeWindow) {
      return;
    }

    const targetDocument = iframeWindow.document;

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }
      if (bottomDrawerContent) {
        setBottomDrawerContent(null);
      }
    };

    targetDocument.addEventListener("keydown", handleEscapeKey);

    return () => {
      targetDocument.removeEventListener("keydown", handleEscapeKey);
    };
  }, [iframeWindow, bottomDrawerContent]);

  // Update sidebar navigation items from dashboard headings
  useEffect(() => {
    if (!iframeWindow || !mainContentRef.current) {
      setSidebarNavItems([]);
      return;
    }

    const main = mainContentRef.current;
    const dashboardTitle = dashboardTitleRef.current;

    const timer = setTimeout(() => {
      const headings = main.querySelectorAll("h2");
      const items: Array<{ id: string; label: string; element: HTMLElement }> = [];

      if (dashboardTitle) {
        items.push({
          id: "overview",
          label: "Overview",
          element: dashboardTitle,
        });
      }

      headings.forEach((heading, index) => {
        const text = heading.textContent || "";
        if (text.trim()) {
          items.push({
            id: `heading-${index}`,
            label: text,
            element: heading,
          });
        }
      });

      setSidebarNavItems(items);
    }, 100);

    return () => clearTimeout(timer);
  }, [
    dashboardData?.content_config?.content_html,
    dashboardData?.content_config?.content_spec,
    iframeWindow,
  ]);

  // Bottom drawer modal content
  useEffect(() => {
    if (!iframeWindow) {
      return;
    }
    // Expose setBottomDrawerContent function to the iframe content window for drill-down
    // data table modals, etc.
    iframeWindow.setBottomDrawerContent = (content: HTMLElement[] | null) => {
      if (bottomDrawerContentContainerRef.current) {
        bottomDrawerContentContainerRef.current.innerHTML = "";
        content?.forEach(element => {
          bottomDrawerContentContainerRef.current?.appendChild(element);
        });
      }
      setBottomDrawerContent(content);
    };

    // Expose a helper so iframe content (e.g. table block actions) can open the chat sidebar.
    iframeWindow.openDashboardChat = () => {
      if (isEditingEnabled) {
        setDashboardMode("editor");
      }
      setIsChatSidebarOpen(true);
    };

    iframeWindow.openDashboardChatWithBlockChip = (
      blockId: string,
      blockTitle: string,
      blockType: string
    ) => {
      if (isEditingEnabled) {
        setDashboardMode("editor");
      }
      setIsChatSidebarOpen(true);
      requestAnimationFrame(() => {
        chatSidebarRef.current?.insertBlockChip(blockId, blockTitle, blockType);
      });
    };

    return () => {
      delete iframeWindow.setBottomDrawerContent;
      delete iframeWindow.openDashboardChat;
      delete iframeWindow.openDashboardChatWithBlockChip;
    };
  }, [iframeWindow, isEditingEnabled, setDashboardMode]);

  // Update browser tab title when dashboard data loads
  useEffect(() => {
    if (!dashboardData) {
      return;
    }

    const title = dashboardData.content_config?.title || "Dashboard";
    document.title = `${title} | Coefficient AI`;
  }, [dashboardData]);

  const handleEditButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if ("blur" in e.currentTarget) {
      (e.currentTarget as HTMLButtonElement).blur();
    }

    if (isDashboardCreateMode) {
      return;
    }

    if (trialExpired) {
      setIsChatUnavailableModalOpen(true);
      return;
    }

    // Toggle chat sidebar open state
    if (isChatSidebarOpen) {
      setIsChatSidebarOpen(false);
    } else {
      // In editor mode, ensure we're in editor when opening the sidebar.
      // In viewer mode, just open the "Ask Coefficient AI" sidebar without changing mode.
      if (isEditingEnabled) {
        setDashboardMode("editor");
      }

      setIsChatSidebarOpen(true);

      // Track edit chat viewed
      if (dashboardData) {
        track(AnalyticsEventNameType.VibeDashEditChatViewed, {
          vibe_dash_id: dashboardId,
          ss_id: dashboardData.spreadsheet_id,
          vibe_dash_name: dashboardData.content_config?.title,
          vibe_dash_slicers: slicerNames,
        });
      }
    }
  };

  const handleAutoFixExhausted = useCallback((summary: string) => {
    setToastProps({
      message: summary || "Auto-fix couldn't resolve the dashboard error after 3 attempts.",
      type: "warning",
      materialIcon: "warning",
      duration: 6000,
      onClose: () => setToastProps(null),
    });
  }, []);

  const handleBottomDrawerBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      setBottomDrawerContent(null);
    }
  };

  const handleSignOut = async () => {
    try {
      const response = await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
      // Ensure MSAL session doesn't silently re-auth on reload.
      await msalInstance.clearCache();
      if (response.ok) {
        window.location.reload();
      } else {
        console.error("Failed to sign out");
      }
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  /**
   * The persistence half of applyDashboardChanges: given the dashboard as it was BEFORE an
   * edit (`baselineDashboardData`) and the config to save, push the undo entry, PUT, and on
   * failure roll the cache back to the baseline. The optimistic cache write is the caller's
   * job — most callers do it via applyDashboardChanges in the same breath, but the block edit
   * popover writes locally on every click and calls this once when its session ends, so the
   * two halves have to be callable apart.
   */
  const persistDashboardConfig = useCallback(
    async (
      baselineDashboardData: GetWebDashboard200Response,
      newContentConfig: WebDashboardContentConfig,
      options?: {
        showToast?: boolean;
        onSuccess?: () => void;
        /** Called after a failed backend save, once the optimistic update has been rolled back. */
        onError?: () => void;
        undoEntryId?: string;
      }
    ) => {
      if (!dashboardId) {
        return;
      }

      // Push the pre-edit state to the undo stack. Use a unique ID so the error handler can
      // remove this specific entry (prevents a race if another save starts while this one is
      // in flight). If undoEntryId is provided, use that (for message-to-undo mapping).
      const undoEntryId = options?.undoEntryId ?? crypto.randomUUID();
      setUndoStack(prev => [
        ...prev,
        {
          id: undoEntryId,
          contentConfig: baselineDashboardData.content_config!,
          spreadsheetVersionId: baselineDashboardData.spreadsheet_version_id,
        },
      ]);

      // Persist to backend (no need to send content_html and js_code as they're derived)
      const contentConfigToSave: UpdateWebDashboardRequest["content_config"] = {
        ...newContentConfig,
        content_html: undefined,
        js_code: undefined,
      };

      try {
        const saveResult = await saveDashboardConfigMutation.mutateAsync({
          content_config: contentConfigToSave,
          spreadsheet_version_id: baselineDashboardData.spreadsheet_version_id,
          // Per-slice change declaration: on a stale token the server rebases this edit
          // onto the latest row when nobody else touched the same slices, instead of 409ing.
          edit_delta: buildEditDelta(baselineDashboardData.content_config, newContentConfig),
        });

        if (saveResult.merged_concurrent_changes) {
          // The save was rebased onto concurrent changes — every undo snapshot in this tab
          // predates content we've never seen, so restoring one would revert other writers.
          setUndoStack([]);
          setMessageToUndoEntry(new Map());
        }

        // Track dashboard edit
        track(AnalyticsEventNameType.VibeDashEdited, {
          vibe_dash_id: dashboardId,
          ss_id: baselineDashboardData.spreadsheet_id,
          vibe_dash_name: baselineDashboardData.content_config?.title,
          vibe_dash_slicers: slicerNames,
        });

        if (options?.showToast) {
          setToastProps({
            message: "Changes saved",
            type: "success",
            materialIcon: "check_circle",
            duration: 2000,
            onClose: () => setToastProps(null),
          });
        }

        // Call onSuccess callback if provided (for additional tracking, etc.)
        options?.onSuccess?.();
      } catch (error) {
        console.error("Error saving dashboard:", error);

        // Revert the optimistic update to restore consistency with backend
        queryClient.setQueryData([QueryCacheKey.WEB_DASHBOARD, dashboardId], baselineDashboardData);

        if (isSaveConflictError(error)) {
          showSaveConflictToast();
          // A newer version exists; this tab's undo snapshots are stale and restoring one
          // would revert whatever changed — drop them all, not just the failed entry.
          setUndoStack([]);
          setMessageToUndoEntry(new Map());
        } else {
          setToastProps({
            message: "Failed to save changes",
            type: "error",
            materialIcon: "error",
            onClose: () => setToastProps(null),
          });
          // Remove this specific entry from the undo stack since save failed
          // (filter by ID rather than removing last item to handle concurrent saves)
          setUndoStack(prev => prev.filter(entry => entry.id !== undoEntryId));
        }

        options?.onError?.();
      }
    },
    [
      dashboardId,
      queryClient,
      saveDashboardConfigMutation,
      track,
      slicerNames,
      showSaveConflictToast,
    ]
  );

  const applyDashboardChanges = useCallback(
    async (
      computeNewConfig: (
        currentConfig: WebDashboardContentConfig,
        contentSpec: WebDashboardContentSpec
      ) => WebDashboardContentConfig,
      options?: {
        showToast?: boolean;
        onSuccess?: () => void;
        /** Called after a failed backend save, once the optimistic update has been rolled back. */
        onError?: () => void;
        undoEntryId?: string;
        skipSave?: boolean;
      }
    ) => {
      if (!dashboardId) {
        return;
      }

      const originalDashboardData = queryClient.getQueryData([
        QueryCacheKey.WEB_DASHBOARD,
        dashboardId,
      ]) as GetWebDashboard200Response;

      if (!originalDashboardData?.content_config?.content_spec) {
        return;
      }

      const contentSpec = originalDashboardData.content_config
        .content_spec as WebDashboardContentSpec;

      const newContentConfig = computeNewConfig(originalDashboardData.content_config, contentSpec);

      // Skip backend save if skipSave is true (e.g., when backend already saved via AI agent)
      if (options?.skipSave) {
        setUndoStack(prev => [
          ...prev,
          {
            id: options?.undoEntryId ?? crypto.randomUUID(),
            contentConfig: originalDashboardData.content_config!,
            spreadsheetVersionId: originalDashboardData.spreadsheet_version_id,
          },
        ]);

        await queryClient.invalidateQueries({
          queryKey: [QueryCacheKey.WEB_DASHBOARD, dashboardId],
          exact: false,
        });

        // Track dashboard edit
        track(AnalyticsEventNameType.VibeDashEdited, {
          vibe_dash_id: dashboardId,
          ss_id: originalDashboardData.spreadsheet_id,
          vibe_dash_name: originalDashboardData.content_config?.title,
          vibe_dash_slicers: slicerNames,
        });

        if (options?.showToast) {
          setToastProps({
            message: "Changes applied",
            type: "success",
            materialIcon: "check_circle",
            duration: 2000,
            onClose: () => setToastProps(null),
          });
        }
        options?.onSuccess?.();
        return;
      }

      // Optimistic update, then persist against the pre-edit snapshot.
      queryClient.setQueryData([QueryCacheKey.WEB_DASHBOARD, dashboardId], {
        ...originalDashboardData,
        content_config: newContentConfig,
      });

      await persistDashboardConfig(originalDashboardData, newContentConfig, options);
    },
    [dashboardId, queryClient, track, slicerNames, persistDashboardConfig]
  );

  const handleSidebarDashboardUpdate = useCallback(
    async (config: Partial<WebDashboardContentConfig>, messageId?: string) => {
      // Never update dashboard in super admin view mode (read-only)
      if (requestSuperAdminView) {
        return;
      }

      // Generate undo entry ID before calling applyDashboardChanges
      const undoEntryId = crypto.randomUUID();

      // Track the mapping if messageId provided (for per-message revert)
      if (messageId) {
        setMessageToUndoEntry(prev => new Map(prev).set(messageId, undoEntryId));
      }

      await applyDashboardChanges(
        currentConfig => ({
          ...currentConfig,
          ...config,
          // Preserve theme fields — AI responses don't include them and would overwrite with null
          theme_id: currentConfig.theme_id,
          mode: currentConfig.mode,
          background: currentConfig.background,
        }),
        {
          showToast: true,
          undoEntryId,
          skipSave: true, // Backend already saved via update_web_dashboard tool
        }
      );
    },
    [applyDashboardChanges, requestSuperAdminView]
  );

  const handleBlockScreenshotCaptureRequest = useCallback(
    (req: { requestId: string; blockIds: string[] }) => {
      console.info("[vibedash][screenshot] request received", {
        requestId: req.requestId,
        blockIds: req.blockIds,
        ts: Date.now(),
      });
      capturedBlockIdsRef.current = new Set();
      captureInFlightRef.current = new Set();
      captureRetriesRef.current = new Map();
      setBlockScreenshotRequest(req);
    },
    []
  );

  // If the agent run ends (or the sidebar closes), clear any pending screenshot request so we
  // don't accidentally capture on future re-renders using an expired request_id.
  useEffect(() => {
    if (isAiThinking) {
      return;
    }
    if (blockScreenshotRequestRef.current) {
      setBlockScreenshotRequest(null);
      capturedBlockIdsRef.current = new Set();
      captureInFlightRef.current = new Set();
      captureRetriesRef.current = new Map();
    }
  }, [isAiThinking]);

  useEffect(() => {
    if (!iframeWindow || !blockScreenshotRequest) {
      return;
    }

    const requestId = blockScreenshotRequest.requestId;
    const requested = new Set(blockScreenshotRequest.blockIds);
    const dashboardDebounceTimerRef: { current: number | null } = { current: null };
    const requestStartedAtMs = Date.now();
    // (intentionally quiet) capture loop starts when request is set

    const waitForIframePaint = async (frames: number, extraDelayMs = 0, maxWaitMs = 3000) => {
      // Wait for the iframe to paint at least N animation frames. This helps avoid capturing
      // "blank" Chart.js canvases: Chart.js often renders on the next rAF after chart creation.
      await new Promise<void>((resolve, reject) => {
        let remaining = Math.max(0, frames);
        let settled = false;

        const timeoutId = window.setTimeout(() => {
          if (settled) {
            return;
          }
          settled = true;
          reject(new Error(`waitForIframePaint timed out after ${maxWaitMs}ms`));
        }, maxWaitMs);

        const step = () => {
          if (settled) {
            return;
          }
          remaining -= 1;
          if (remaining <= 0) {
            settled = true;
            window.clearTimeout(timeoutId);
            resolve();
            return;
          }
          iframeWindow.requestAnimationFrame(step);
        };
        iframeWindow.requestAnimationFrame(step);
      });
      if (extraDelayMs > 0) {
        await new Promise<void>(resolve => window.setTimeout(resolve, extraDelayMs));
      }
    };

    const registerScreenshotTimeout = async (blockId: string, reason: string) => {
      try {
        const imageType = "image/jpeg";
        const presigned = await presignBlockScreenshotUpload(apiClient, requestId, {
          block_id: blockId,
          content_type: imageType,
        });
        const s3Key = presigned?.s3_key as string | undefined;
        if (!s3Key) {
          throw new Error("Missing s3_key in presign response");
        }

        // IMPORTANT: We intentionally do NOT upload an image in this error path.
        // We register a sentinel key (same prefix) so the backend can stop waiting and
        // report a timeout for this block.
        const safeReason = reason.replace(/[^A-Za-z0-9]/g, "");
        const timeoutS3Key = s3Key.replace(/[^/]+$/, `__timeout__${safeReason}__${Date.now()}.jpg`);
        await registerBlockScreenshotUpload(apiClient, requestId, {
          block_id: blockId,
          s3_key: timeoutS3Key,
        });

        console.warn("[vibedash][screenshot] registered timeout sentinel", {
          requestId,
          blockId,
          reason,
          s3Key: timeoutS3Key,
        });
      } catch (e) {
        console.warn("[vibedash][screenshot] failed to register timeout sentinel", {
          requestId,
          blockId,
          reason,
          e,
        });
      }
    };

    const giveUpOnBlock = async (blockId: string, reason: string) => {
      await registerScreenshotTimeout(blockId, reason);
      capturedBlockIdsRef.current.add(blockId);
      const done = Array.from(requested).every(bid => capturedBlockIdsRef.current.has(bid));
      if (done) {
        console.info(
          "[vibedash][screenshot] all requested completed (with timeouts); clearing request",
          {
            requestId,
          }
        );
        setBlockScreenshotRequest(null);
      }
    };

    const waitForBlockStable = async (
      el: HTMLElement,
      opts: { maxWaitMs: number; settleMs: number; recentRenderGraceMs: number }
    ) => {
      const start = Date.now();
      while (Date.now() - start < opts.maxWaitMs) {
        const inflight = el.dataset?.coeffRenderInflight === "1";
        const lastRenderedRaw = el.dataset?.coeffLastRenderedTs ?? "";
        const lastRenderedTs = Number.parseInt(lastRenderedRaw, 10) || 0;
        const ageMs = lastRenderedTs ? Date.now() - lastRenderedTs : 0;

        // We prefer to capture after a render that happened after this request began.
        // But if the request arrived just after render completion, accept "recent enough"
        // renders to avoid waiting forever.
        const renderedAfterRequest = lastRenderedTs >= requestStartedAtMs;
        const renderedVeryRecently =
          lastRenderedTs > 0 && requestStartedAtMs - lastRenderedTs <= opts.recentRenderGraceMs;

        // If we haven't observed *any* render completion yet, don't capture: this is the
        // exact state that produces "title + grey" screenshots (initial skeleton only).
        const hasEverRendered = lastRenderedTs > 0;
        const isRenderedEnough = hasEverRendered && (renderedAfterRequest || renderedVeryRecently);
        const isSettled = hasEverRendered && ageMs >= opts.settleMs;

        if (!inflight && isRenderedEnough && isSettled) {
          return;
        }
        await new Promise<void>(resolve => iframeWindow.setTimeout(resolve, 50));
      }
    };

    // Retry up to 40 x 250ms (10s), then give up loudly so the backend learns the reason
    // instead of waiting out its own timeout.
    const scheduleRetry = (blockId: string, reason: string) => {
      const retries = (captureRetriesRef.current.get(blockId) ?? 0) + 1;
      captureRetriesRef.current.set(blockId, retries);
      if (retries > 40) {
        if (!captureInFlightRef.current.has(blockId)) {
          captureInFlightRef.current.add(blockId);
          void giveUpOnBlock(blockId, reason).finally(() => {
            captureInFlightRef.current.delete(blockId);
          });
        }
        return;
      }
      window.setTimeout(() => {
        tryCaptureBlock(blockId);
      }, 250);
    };

    const tryCaptureBlock = async (blockId: string) => {
      if (!requested.has(blockId)) {
        return;
      }
      if (capturedBlockIdsRef.current.has(blockId) || captureInFlightRef.current.has(blockId)) {
        return;
      }

      const resolveTargetElement = (targetId: string): HTMLElement | null => {
        // Pseudo-target IDs (not real dashboard blocks)
        if (targetId === "ui:slicers") {
          return iframeWindow.document.getElementById("slicersContainer");
        }
        if (targetId === "ui:dashboard") {
          // Capture the "whole dashboard" area within the iframe.
          // This excludes the surrounding chrome outside the central dashboard container.
          return iframeWindow.document.getElementById("dashboardMainContainer");
        }

        return iframeWindow.document.getElementById(`block_${targetId}`);
      };

      const el = resolveTargetElement(blockId);
      if (!el) {
        console.info("[vibedash][screenshot] target element not found", {
          requestId,
          blockId,
          retries: captureRetriesRef.current.get(blockId) ?? 0,
        });
        // A block that is not in the DOM (other view, modal, deleted) never emits a
        // render-complete event, so without this the server waits out its full timeout.
        scheduleRetry(blockId, "elementNotFound");
        return;
      }

      const html2canvas = iframeWindow.html2canvas;
      if (!html2canvas) {
        console.info("[vibedash][screenshot] html2canvas not loaded yet", {
          requestId,
          blockId,
          retries: captureRetriesRef.current.get(blockId) ?? 0,
        });
        scheduleRetry(blockId, "html2canvasNotLoaded");
        return;
      }

      captureInFlightRef.current.add(blockId);
      try {
        // Give the iframe a moment to paint recent changes before capturing.
        // Especially important for Chart.js (canvas) blocks which may draw on rAF.
        const hasCanvas = Boolean(el.querySelector?.("canvas"));
        const tPaint0 = performance.now();
        try {
          await waitForIframePaint(2, hasCanvas ? 800 : 400, 3000);
        } catch (e) {
          // requestAnimationFrame is suspended in a hidden tab, so record visibility: it tells
          // the backend log whether the user tabbed away or the paint is genuinely stuck.
          const tabHidden = document.visibilityState === "hidden";
          console.warn("[vibedash][screenshot] waitForIframePaint timed out", {
            requestId,
            blockId,
            hasCanvas,
            tabHidden,
            e,
          });
          await giveUpOnBlock(blockId, tabHidden ? "paintTimeoutTabHidden" : "paintTimeout");
          return;
        }
        console.info("[vibedash][screenshot] waitForIframePaint done", {
          requestId,
          blockId,
          hasCanvas,
          ms: Math.round(performance.now() - tPaint0),
        });

        const isWholeDashboard = blockId === "ui:dashboard";
        const imageType = "image/jpeg";
        const jpegQuality = isWholeDashboard ? 0.5 : 0.85;

        const cacheBustToken = Date.now();
        const tH2c0 = performance.now();
        let canvas = await html2canvas(el, {
          backgroundColor: "#ffffff",
          useCORS: true,
          // IMPORTANT: avoid globally cache-busting ALL images (e.g. third-party avatars),
          // which can cause them to be missing at capture time. We selectively cache-bust
          // only our own S3-hosted dashboard images in `onclone` below.
          cacheBust: false,
          onclone: (clonedDoc: Document) => {
            try {
              const rootId = (el as any)?.id as string | undefined;
              const root =
                (rootId ? clonedDoc.getElementById(rootId) : null) ??
                clonedDoc.body ??
                clonedDoc.documentElement;

              const imgs = root?.querySelectorAll?.("img") ?? [];
              imgs.forEach(img => {
                const src = img.src;
                if (!src || !/^https?:\/\//i.test(src)) {
                  return;
                }
                // Only force CORS + cache-bust for our own dashboard images (S3/CDN).
                // Doing this for third-party images (e.g. pbs.twimg.com) can race/replace the
                // cached image load and result in "hollow" avatars in screenshots.
                try {
                  const u = new URL(src);
                  const path = u.pathname || "";
                  const isOurDashboardImage =
                    path.startsWith("/web-dashboard-images/") ||
                    path.startsWith("/web-dashboard-block-screenshots/");
                  if (!isOurDashboardImage) {
                    return;
                  }
                } catch {
                  return;
                }

                img.crossOrigin = "anonymous";
                const joiner = src.includes("?") ? "&" : "?";
                img.src = `${src}${joiner}cb=${cacheBustToken}`;
              });
            } catch {
              // Best-effort only.
            }
          },
          // Keep screenshots smaller/faster for LLM verification.
          // Note: scale is output pixel density, not CSS zoom.
          scale: 1,
        } as any);
        console.info("[vibedash][screenshot] html2canvas done", {
          requestId,
          blockId,
          ms: Math.round(performance.now() - tH2c0),
          width: canvas.width,
          height: canvas.height,
          isWholeDashboard,
          jpegQuality,
        });

        // Downscale whole-dashboard captures to keep the image small for the LLM.
        if (isWholeDashboard && canvas.width > 1200) {
          const ratio = 1200 / canvas.width;
          const targetW = Math.round(canvas.width * ratio);
          const targetH = Math.round(canvas.height * ratio);
          const offscreen = document.createElement("canvas");
          offscreen.width = targetW;
          offscreen.height = targetH;
          const ctx = offscreen.getContext("2d");
          if (ctx) {
            ctx.drawImage(canvas, 0, 0, targetW, targetH);
            canvas = offscreen;
          }
        }

        const blob: Blob = await new Promise((resolve, reject) => {
          canvas.toBlob(
            b => {
              if (!b) {
                reject(new Error("Failed to encode screenshot JPEG"));
                return;
              }
              resolve(b);
            },
            imageType,
            jpegQuality
          );
        });

        // 1) Ask backend for a presigned S3 upload (POST) + final screenshot_url.
        const presigned = await presignBlockScreenshotUpload(apiClient, requestId, {
          block_id: blockId,
          content_type: imageType,
        });
        const uploadUrl = presigned?.upload_url as string | undefined;
        const uploadFields = presigned?.upload_fields as Record<string, string> | undefined;
        const s3Key = presigned?.s3_key as string | undefined;
        if (!uploadUrl || !uploadFields || !s3Key) {
          throw new Error("Invalid presign response for block screenshot upload");
        }

        // 2) Upload directly to S3 using presigned POST.
        await uploadToPresignedS3Post(uploadUrl, uploadFields, blob, `block_${blockId}.jpg`);

        // 3) Tell backend the screenshot is ready by registering s3_key in Redis.
        await registerBlockScreenshotUpload(apiClient, requestId, {
          block_id: blockId,
          s3_key: s3Key,
        });

        capturedBlockIdsRef.current.add(blockId);

        // Auto-finish: once we've captured all requested blocks, clear the request.
        const done = Array.from(requested).every(bid => capturedBlockIdsRef.current.has(bid));
        if (done) {
          console.info("[vibedash][screenshot] all requested captured; clearing request", {
            requestId,
          });
          setBlockScreenshotRequest(null);
        }
      } catch (e) {
        // Best-effort: allow retries on subsequent render events.
        console.warn("Failed to capture/upload block screenshot", { blockId, requestId, e });
      } finally {
        captureInFlightRef.current.delete(blockId);
      }
    };

    const onBlockRenderComplete = (event: Event) => {
      const detail = (event as any)?.detail as { blockId?: string } | undefined;
      const blockId = detail?.blockId;
      if (typeof blockId === "string" && blockId) {
        tryCaptureBlock(blockId);
      }

      // If the request includes a whole-dashboard capture, debounce it until the
      // stream of block render-complete events quiets down.
      if (requested.has("ui:dashboard") && !capturedBlockIdsRef.current.has("ui:dashboard")) {
        if (dashboardDebounceTimerRef.current) {
          window.clearTimeout(dashboardDebounceTimerRef.current);
        }
        dashboardDebounceTimerRef.current = window.setTimeout(() => {
          tryCaptureBlock("ui:dashboard");
        }, 500);
      }
    };

    // Listen for per-render completion events emitted from the iframe window.
    // (The iframe document is replaced on srcDoc reloads, but the window stays stable.)
    iframeWindow.addEventListener("coeff:block_render_complete", onBlockRenderComplete as any);

    // Also attempt immediate capture in case blocks rendered before the request arrived.
    window.setTimeout(() => {
      blockScreenshotRequest.blockIds.forEach(bid => {
        if (bid === "ui:dashboard") {
          // Give the iframe a moment to render blocks before capturing the whole dashboard.
          window.setTimeout(() => tryCaptureBlock(bid), 750);
          return;
        }
        tryCaptureBlock(bid);
      });
    }, 0);

    return () => {
      iframeWindow.removeEventListener("coeff:block_render_complete", onBlockRenderComplete as any);
      if (dashboardDebounceTimerRef.current) {
        window.clearTimeout(dashboardDebounceTimerRef.current);
      }
    };
  }, [iframeWindow, blockScreenshotRequest]);

  const handleLayoutChange = useCallback(
    (layout: DashboardLayout) => {
      applyDashboardChanges((currentConfig, contentSpec) =>
        applyLayoutChange(currentConfig, contentSpec, layout)
      );
    },
    [applyDashboardChanges]
  );

  /** The block as currently persisted, read live from the query cache. */
  const getSpecBlock = useCallback(
    (blockId: string): WebDashboardContentSpecBlocksInner | undefined => {
      const currentData = queryClient.getQueryData<GetWebDashboard200Response>([
        QueryCacheKey.WEB_DASHBOARD,
        dashboardId,
      ]);
      const spec = currentData?.content_config?.content_spec as WebDashboardContentSpec | undefined;
      return spec?.blocks?.find(b => b.block_id === blockId);
    },
    [dashboardId, queryClient]
  );

  // Column drags go through a serialized, coalescing save queue. See useBlockPatchQueue for
  // why concurrent applyDashboardChanges calls race.
  const { queueBlockPatch } = useBlockPatchQueue({
    applyDashboardChanges,
    restoreTableSessionOverrides,
  });

  // The edit popover's click controls: every click writes the cache so the block updates on
  // the spot, and ONE save fires when the popover closes (see createBlockEditSession).
  const blockEditSession = useBlockEditSession<GetWebDashboard200Response>({
    getSnapshot: () => {
      const data = queryClient.getQueryData<GetWebDashboard200Response>([
        QueryCacheKey.WEB_DASHBOARD,
        dashboardId,
      ]);
      return data?.content_config?.content_spec ? data : undefined;
    },
    applyLocal: (blockId, patch) => {
      queryClient.setQueryData<GetWebDashboard200Response>(
        [QueryCacheKey.WEB_DASHBOARD, dashboardId],
        data => {
          const spec = data?.content_config?.content_spec as WebDashboardContentSpec | undefined;
          if (!data?.content_config || !spec) {
            return data;
          }
          return {
            ...data,
            content_config: applyBlockPatches(
              data.content_config,
              spec,
              new Map([[blockId, patch]])
            ),
          };
        }
      );
    },
    hasBlockChanged: (baseline, blockId) => {
      const findBlock = (data: GetWebDashboard200Response | undefined) =>
        (data?.content_config?.content_spec as WebDashboardContentSpec | undefined)?.blocks?.find(
          b => b.block_id === blockId
        );
      const current = queryClient.getQueryData<GetWebDashboard200Response>([
        QueryCacheKey.WEB_DASHBOARD,
        dashboardId,
      ]);
      const before = findBlock(baseline);
      const after = findBlock(current);
      // Blocks are plain spec JSON, so a stringify compare is exact and cheap at this size.
      return before !== after && JSON.stringify(before) !== JSON.stringify(after);
    },
    persist: async baseline => {
      const current = queryClient.getQueryData<GetWebDashboard200Response>([
        QueryCacheKey.WEB_DASHBOARD,
        dashboardId,
      ]);
      if (!current?.content_config) {
        return;
      }
      await persistDashboardConfig(baseline, current.content_config);
    },
  });

  // A tab close mid-session still saves. Best-effort: the browser may not let the request
  // finish, so the real safety net is the popover committing on unmount.
  useEffect(() => {
    const handleBeforeUnload = () => {
      void blockEditSession.commitAll();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [blockEditSession]);

  // Persist a data table's manual column adjustments (drag-resize / drag-reorder) into the
  // block's `table_overrides`. Saved silently — a toast on every column drag would be noise
  // — but still lands on the undo stack like any other edit.
  const handleTableOverridesChange = useCallback(
    (blockId: string, overrides: DataTableUserOverrides) => {
      queueBlockPatch(blockId, { table_overrides: overrides });
    },
    [queueBlockPatch]
  );

  /**
   * Persists the dashboard's own title and description (the hero <h1> and <p>).
   *
   * Writes the top-level content_config pair AND mirrors them into
   * content_spec[title|description], the same way the backend's PUT handler does via
   * sync_dashboard_title_description. The mirroring is load-bearing, not belt-and-braces: the
   * AI editor rebuilds content_config.title from content_spec on every edit, so a half-written
   * rename would silently revert on the user's next AI edit. Doing it here too keeps the cache
   * identical to the saved row without a post-save refetch (see useSaveDashboardConfigMutation
   * for why saves no longer refetch).
   */
  const handleEditDashboardHeader = useCallback(
    ({ title, description }: DashboardHeaderEdits) => {
      applyDashboardChanges((currentConfig, spec) => ({
        ...currentConfig,
        title,
        description,
        content_spec: { ...spec, title, description },
      }));
    },
    [applyDashboardChanges]
  );

  // The edit popover's click controls (icon, chart type, legend, axes, ...) and, on Done, its
  // text fields. Writes the cache only — the block updates on the spot and the popover stays
  // open. Nothing is saved until handleCommitBlockEdits runs at the end of the session.
  const handleApplyBlockEditLocally = useCallback(
    (blockId: string, updates: BlockEditUpdates) => {
      const block = getSpecBlock(blockId);
      if (!block) {
        return;
      }
      blockEditSession.applyLocal(blockId, buildBlockSpecPatch(block.type, updates));
    },
    [blockEditSession, getSpecBlock]
  );

  // Ends the popover session for a block: one PUT covering every local edit since it opened,
  // or nothing at all if the user changed nothing. Silent: the changes are already visible
  // in the block, which is its own confirmation.
  const handleCommitBlockEdits = useCallback(
    (blockId: string) => blockEditSession.commit(blockId),
    [blockEditSession]
  );

  const handleUndo = useCallback(
    async (options?: { showToast?: boolean }) => {
      if (undoStack.length === 0 || !dashboardId) {
        return;
      }

      const previousState = undoStack[undoStack.length - 1];

      // Get the current state before attempting undo, so we can restore it if the mutation fails
      const currentDashboardData = queryClient.getQueryData<GetWebDashboard200Response>([
        QueryCacheKey.WEB_DASHBOARD,
        dashboardId,
      ]);

      if (!currentDashboardData) {
        return;
      }

      // Track undo click
      track(AnalyticsEventNameType.VibeDashUndoClicked, {
        vibe_dash_id: dashboardId,
        ss_id: currentDashboardData.spreadsheet_id,
        vibe_dash_name: currentDashboardData.content_config?.title,
        vibe_dash_slicers: slicerNames,
      });

      // Store current theme in case we need to restore on error
      const currentThemeId = themeId;
      const currentThemeMode = themeMode;
      const currentThemeBackground = themeBackground;

      try {
        queryClient.setQueryData([QueryCacheKey.WEB_DASHBOARD, dashboardId], (oldData: any) => ({
          ...oldData,
          content_config: previousState.contentConfig,
          spreadsheet_version_id: previousState.spreadsheetVersionId,
        }));

        // Restore the theme trio from the snapshot. Legacy snapshots may omit any of the three,
        // so fall back to the legacy→palette seed / defaults instead of KEEPING the newer local
        // values — a stale local mode would make the next palette pick apply the wrong mode.
        {
          const restoredThemeId = previousState.contentConfig.theme_id ?? DEFAULT_THEME_ID;
          const restoredSeed = LEGACY_THEME_TO_PALETTE[restoredThemeId];
          const restoredMode =
            (previousState.contentConfig.mode as ThemeMode | undefined) ??
            restoredSeed?.mode ??
            DEFAULT_THEME_MODE;
          const restoredBackground =
            (previousState.contentConfig.background as ThemeBackground | undefined) ??
            restoredSeed?.background ??
            DEFAULT_THEME_BACKGROUND;
          if (restoredThemeId !== themeId) {
            setThemeId(restoredThemeId);
          }
          if (restoredMode !== themeMode) {
            setThemeMode(restoredMode);
          }
          if (restoredBackground !== themeBackground) {
            setThemeBackground(restoredBackground);
          }
        }

        const contentConfigToSave: UpdateWebDashboardRequest["content_config"] = {
          ...previousState.contentConfig,
          content_html: undefined,
          js_code: undefined,
        };

        await saveDashboardConfigMutation.mutateAsync({
          content_config: contentConfigToSave,
          spreadsheet_version_id: previousState.spreadsheetVersionId,
        });

        // Remove this specific entry from the undo stack after successful undo
        // (filter by ID rather than removing last item to handle concurrent saves)
        setUndoStack(prev => prev.filter(entry => entry.id !== previousState.id));

        if (options?.showToast !== false) {
          setToastProps({
            message: "Changes undone",
            type: "info",
            materialIcon: "check_circle",
            duration: 2000,
            onClose: () => setToastProps(null),
          });
        }
      } catch (error) {
        console.error("Error undoing changes:", error);

        // Restore the cache to the state before the undo attempt
        queryClient.setQueryData([QueryCacheKey.WEB_DASHBOARD, dashboardId], currentDashboardData);

        // Restore theme state on error
        setThemeId(currentThemeId);
        setThemeMode(currentThemeMode);
        setThemeBackground(currentThemeBackground);

        if (isSaveConflictError(error)) {
          showSaveConflictToast();
          // A newer version exists — every snapshot in this tab's undo stack is stale
          // and restoring any of them would revert whatever changed.
          setUndoStack([]);
          setMessageToUndoEntry(new Map());
        } else {
          setToastProps({
            message: "Failed to undo changes",
            type: "error",
            materialIcon: "error",
            onClose: () => setToastProps(null),
          });
        }
      }
    },
    [
      undoStack,
      dashboardId,
      queryClient,
      saveDashboardConfigMutation,
      themeId,
      themeMode,
      themeBackground,
      track,
      slicerNames,
      showSaveConflictToast,
    ]
  );

  // Keep a ref to handleUndo so toast action closures always call the latest version
  const handleUndoRef = useRef(handleUndo);
  handleUndoRef.current = handleUndo;

  // Handler to revert dashboard to state before a specific chat message's changes
  // This directly restores to the target state in one operation (more efficient and avoids stale closure issues)
  // agentMessageId: used to look up the undo entry in the stack
  // userMessageId: sent to backend to mark messages as reverted
  const handleRevertToMessage = useCallback(
    async (agentMessageId: string, userMessageId: string, threadId: string) => {
      const undoEntryId = messageToUndoEntry.get(agentMessageId);
      if (!undoEntryId || !dashboardId) {
        return;
      }

      // Find index of this undo entry in the stack
      const entryIndex = undoStack.findIndex(e => e.id === undoEntryId);
      if (entryIndex === -1) {
        return;
      }

      // Get the target state to restore to
      const targetState = undoStack[entryIndex];

      // Get current state for error recovery
      const currentDashboardData = queryClient.getQueryData<GetWebDashboard200Response>([
        QueryCacheKey.WEB_DASHBOARD,
        dashboardId,
      ]);

      if (!currentDashboardData) {
        return;
      }

      const currentThemeId = themeId;
      const currentThemeMode = themeMode;
      const currentThemeBackground = themeBackground;

      try {
        // Restore to target state
        queryClient.setQueryData([QueryCacheKey.WEB_DASHBOARD, dashboardId], (oldData: any) => ({
          ...oldData,
          content_config: targetState.contentConfig,
          spreadsheet_version_id: targetState.spreadsheetVersionId,
        }));

        // Restore the theme trio from the snapshot (legacy snapshots may omit fields — fall back
        // to the legacy→palette seed / defaults rather than keeping newer local values).
        {
          const restoredThemeId = targetState.contentConfig.theme_id ?? DEFAULT_THEME_ID;
          const restoredSeed = LEGACY_THEME_TO_PALETTE[restoredThemeId];
          const restoredMode =
            (targetState.contentConfig.mode as ThemeMode | undefined) ??
            restoredSeed?.mode ??
            DEFAULT_THEME_MODE;
          const restoredBackground =
            (targetState.contentConfig.background as ThemeBackground | undefined) ??
            restoredSeed?.background ??
            DEFAULT_THEME_BACKGROUND;
          if (restoredThemeId !== themeId) {
            setThemeId(restoredThemeId);
          }
          if (restoredMode !== themeMode) {
            setThemeMode(restoredMode);
          }
          if (restoredBackground !== themeBackground) {
            setThemeBackground(restoredBackground);
          }
        }

        // Save to backend with revert metadata to persist the revert event
        await saveDashboardConfigMutation.mutateAsync({
          content_config: {
            ...targetState.contentConfig,
            content_html: undefined,
            js_code: undefined,
          },
          spreadsheet_version_id: targetState.spreadsheetVersionId,
          update_type: WebDashboardUpdateType.Revert,
          revert_to_message_id: userMessageId,
          thread_id: threadId,
        });

        // Remove all undo entries from this point onwards (inclusive)
        setUndoStack(prev => prev.slice(0, entryIndex));
      } catch (error) {
        console.error("Error reverting to message:", error);
        // Restore on error
        queryClient.setQueryData([QueryCacheKey.WEB_DASHBOARD, dashboardId], currentDashboardData);
        setThemeId(currentThemeId);
        setThemeMode(currentThemeMode);
        setThemeBackground(currentThemeBackground);

        if (isSaveConflictError(error)) {
          showSaveConflictToast();
          // A newer version exists — this tab's undo snapshots are stale.
          setUndoStack([]);
          setMessageToUndoEntry(new Map());
        }
      }
    },
    [
      messageToUndoEntry,
      undoStack,
      dashboardId,
      queryClient,
      saveDashboardConfigMutation,
      themeId,
      themeMode,
      themeBackground,
      showSaveConflictToast,
    ]
  );

  const handleBlockDelete = useCallback(
    (blockId: string, newLayout: DashboardLayout) => {
      // Get current dashboard data to extract block info for tracking
      // (must capture before save since the block will be removed)
      const currentDashboardData = queryClient.getQueryData<GetWebDashboard200Response>([
        QueryCacheKey.WEB_DASHBOARD,
        dashboardId,
      ]);

      const contentSpec = currentDashboardData?.content_config?.content_spec as
        | WebDashboardContentSpec
        | undefined;
      const block = contentSpec?.blocks?.find(b => b.block_id === blockId);

      // Get block title (metric cards use 'label', others use 'title')
      const blockTitle =
        block?.type === "metric_card" ? (block as any).label : (block as any)?.title;

      // Capture block type label before deletion for toast message
      const blockTypeLabel = getBlockTypeLabel(block?.type);

      // Capture tracking data before delete (block won't exist after save)
      const trackingData = {
        vibe_dash_id: dashboardId,
        vibe_dash_block_id: blockId,
        vibe_dash_block_type: block?.type,
        vibe_dash_block_title: blockTitle,
        ss_id: currentDashboardData?.spreadsheet_id,
        vibe_dash_name: currentDashboardData?.content_config?.title,
        vibe_dash_slicers: slicerNames,
      };

      applyDashboardChanges(
        (currentConfig, spec) => ({
          ...currentConfig,
          content_spec: {
            ...spec,
            blocks: spec.blocks.filter(b => b.block_id !== blockId),
            layout: newLayout,
          },
        }),
        {
          // Track block deletion and show undo toast after successful save
          onSuccess: () => {
            track(AnalyticsEventNameType.VibeDashBlockDeleted, trackingData);

            // Show deletion toast with undo action
            setToastProps({
              message: `${blockTypeLabel} deleted`,
              type: "info",
              materialIcon: "info",
              duration: 5000,
              position: "bottom",
              action: {
                label: "Undo change",
                onClick: () => handleUndoRef.current({ showToast: false }),
              },
              onClose: () => setToastProps(null),
            });
          },
        }
      );
    },
    [applyDashboardChanges, dashboardId, queryClient, track, slicerNames]
  );

  // "Also delete affected charts" in the Source Sheet Disable Tab modal (ENG-7204).
  const handleDeleteBlocksForTabDisable = useCallback(
    (blockIds: string[]) => {
      if (blockIds.length === 0) {
        return;
      }
      const blockIdSet = new Set(blockIds);
      applyDashboardChanges(
        (currentConfig, spec) => {
          let layout = spec.layout ?? { rows: [] };
          for (const blockId of blockIds) {
            layout = removeBlockFromLayout(layout, blockId);
          }

          return {
            ...currentConfig,
            content_spec: {
              ...spec,
              blocks: spec.blocks.filter(b => !blockIdSet.has(b.block_id)),
              layout,
            },
          };
        },
        {
          onSuccess: () => {
            setToastProps({
              message: `${blockIds.length} ${blockIds.length === 1 ? "chart" : "charts"} deleted`,
              type: "success",
              materialIcon: "check_circle",
              duration: 5000,
              position: "bottom",
              action: {
                label: "Undo change",
                onClick: () => handleUndoRef.current({ showToast: false }),
              },
              onClose: () => setToastProps(null),
            });
          },
        }
      );
    },
    [applyDashboardChanges]
  );

  const escapeForBlockRef = (str: string) => str.replace(/\\/g, "\\\\").replace(/"/g, '\\"');

  const buildBlockRefTag = useCallback(
    (blockId: string): string => {
      try {
        const currentData = queryClient.getQueryData<GetWebDashboard200Response>([
          QueryCacheKey.WEB_DASHBOARD,
          dashboardId,
        ]);
        const spec = currentData?.content_config?.content_spec as
          | WebDashboardContentSpec
          | undefined;
        if (spec?.blocks) {
          const block = spec.blocks.find(b => b.block_id === blockId);
          if (block) {
            return `[block id="${escapeForBlockRef(block.block_id)}" title="${escapeForBlockRef(
              resolveBlockTitle(block)
            )}"]`;
          }
        }
      } catch {
        // Ignore errors in context building
      }
      return "";
    },
    [dashboardId, queryClient]
  );

  // The AI/chat routes out of the edit popover, plus the text-block popover's save. The block
  // edit popover's own controls go through handleApplyBlockEditLocally + handleCommitBlockEdits.
  const handleEditBlock = useCallback(
    async (blockId: string, updates: BlockEditUpdates) => {
      if (!dashboardId) {
        return;
      }

      // The agent reads the DB, so an open popover session's local edits must land before a
      // chat message about the block goes out. No-op when there is no session.
      if (updates.openChat || updates.aiEditPrompt) {
        await blockEditSession.commit(blockId);
      }

      if (updates.openChat) {
        const currentData = queryClient.getQueryData<GetWebDashboard200Response>([
          QueryCacheKey.WEB_DASHBOARD,
          dashboardId,
        ]);
        const spec = currentData?.content_config?.content_spec as
          | WebDashboardContentSpec
          | undefined;
        setIsChatSidebarOpen(true);
        if (spec) {
          const ctx = getBlockContext(blockId, spec);
          if (ctx) {
            // Insert block chip into the chat input via ref (after sidebar opens)
            requestAnimationFrame(() => {
              chatSidebarRef.current?.insertBlockChip(
                ctx.blockId,
                ctx.blockTitle,
                ctx.blockType ?? "chart"
              );
            });
          }
        }
        return;
      }

      // For AI edit prompts, route through the chat sidebar instead
      if (updates.aiEditPrompt) {
        // If markdown was also provided, save it first before routing to chat
        if (updates.markdown !== undefined) {
          applyDashboardChanges((currentConfig, spec) => ({
            ...currentConfig,
            content_spec: {
              ...spec,
              blocks: spec.blocks.map(b =>
                b.block_id === blockId
                  ? ({
                      ...b,
                      ...buildBlockSpecPatch(b.type, { markdown: updates.markdown }),
                    } as WebDashboardContentSpecBlocksInner)
                  : b
              ),
            },
          }));
        }

        const blockRef = buildBlockRefTag(blockId);
        const message = `${blockRef ?? ""} ${updates.aiEditPrompt}`;

        setIsChatSidebarOpen(true);

        // Draft mode (the AI-convert targets): seed the composer with the prompt — the
        // block ref renders as a chip — and let the user read it and press send. The
        // conversion rewrites the block, so it is not fired silently.
        if (updates.aiEditDraft) {
          prefillComposer(message.trim());
          return;
        }

        // Auto-submit the AI edit prompt to the chat sidebar
        setPendingMessages(prev => [
          ...prev,
          {
            id: `pending-ai-edit-${Date.now()}`,
            role: "user" as const,
            content: message,
            autoSubmit: true,
          },
        ]);
        return;
      }

      applyDashboardChanges((currentConfig, spec) => ({
        ...currentConfig,
        content_spec: {
          ...spec,
          blocks: spec.blocks.map(b =>
            b.block_id === blockId
              ? ({
                  ...b,
                  ...buildBlockSpecPatch(b.type, updates),
                } as WebDashboardContentSpecBlocksInner)
              : b
          ),
        },
      }));
    },
    [dashboardId, applyDashboardChanges, queryClient, prefillComposer, blockEditSession]
  );

  const applyLayoutChange = (
    currentConfig: WebDashboardContentConfig,
    spec: WebDashboardContentSpec,
    newLayout: DashboardLayout
  ): WebDashboardContentConfig => {
    return {
      ...currentConfig,
      content_spec: {
        ...spec,
        blocks: reorderBlocksToLayout(spec.blocks, newLayout),
        layout: newLayout,
      },
    };
  };

  const handleAddBlock = useCallback(
    (
      blockType: string,
      insertAtRowIndex: number,
      prompt?: string,
      adjacentContext?: { block: WebDashboardContentSpecBlocksInner; placement: "after" | "below" }
    ) => {
      if (!dashboardId) {
        return;
      }

      if (blockType === "section_header") {
        let sectionTitle = "";
        let sectionDescription = "";
        if (prompt) {
          try {
            const parsed = JSON.parse(prompt);
            sectionTitle = parsed.title ?? "";
            sectionDescription = parsed.description ?? "";
          } catch {
            sectionTitle = prompt;
          }
        }

        const blockId = `sectionHeader-${Date.now()}`;
        applyDashboardChanges((currentConfig, spec) => {
          const newBlock = {
            block_id: blockId,
            type: "section_header" as const,
            title: sectionTitle,
            description: sectionDescription,
            grid_cols: 4,
          };

          const currentLayout = resolveEffectiveLayout(spec.layout, spec.blocks);
          // when click in between the blocks, place it in the next row.
          const effectiveRowIndex =
            insertAtRowIndex + (adjacentContext?.placement === "after" ? 1 : 0);
          const newRows = [...currentLayout.rows];
          newRows.splice(effectiveRowIndex, 0, {
            rowId: `row-${currentLayout.rows.length}`,
            columns: [{ id: `col-${Date.now()}`, blockIds: [blockId] }],
            height: 80,
            flexWidths: [12],
          });
          newRows.forEach((r, i) => {
            r.rowId = `row-${i}`;
          });
          return applyLayoutChange(
            currentConfig,
            { ...spec, blocks: [...spec.blocks, newBlock] },
            { rows: newRows }
          );
        });
      } else if (blockType === "text_block" && prompt !== undefined) {
        const markdown = prompt;

        const blockId = `textBlock-${Date.now()}`;

        applyDashboardChanges((currentConfig, spec) => {
          const newBlock = {
            block_id: blockId,
            type: "text_block" as const,
            markdown: markdown || "",
            grid_cols: 4,
          };

          const currentLayout = resolveEffectiveLayout(spec.layout, spec.blocks);
          const newRows = currentLayout.rows.map(r => ({
            ...r,
            columns: r.columns.map(c => ({ ...c, blockIds: [...c.blockIds] })),
            flexWidths: r.flexWidths ? [...r.flexWidths] : undefined,
          }));

          if (
            adjacentContext?.placement === "after" &&
            adjacentContext.block &&
            newRows[insertAtRowIndex]
          ) {
            const row = newRows[insertAtRowIndex];
            const adjacentBlockId = adjacentContext.block.block_id;
            const colIndex = row.columns.findIndex(c => c.blockIds.includes(adjacentBlockId));
            const insertColIndex = colIndex !== -1 ? colIndex + 1 : row.columns.length;
            row.columns.splice(insertColIndex, 0, {
              id: `col-${Date.now()}`,
              blockIds: [blockId],
            });
            const currentWidths = row.flexWidths ?? row.columns.map(() => 4);
            const newColWidth = 4;
            currentWidths.splice(insertColIndex, 0, newColWidth);
            const total = currentWidths.reduce((s, w) => s + w, 0);
            row.flexWidths = currentWidths.map(w => Math.max(2, Math.round((w / total) * 12)));
            const snapTotal = row.flexWidths.reduce((s, w) => s + w, 0);
            if (snapTotal !== 12) {
              const largest = row.flexWidths.indexOf(Math.max(...row.flexWidths));
              row.flexWidths[largest] += 12 - snapTotal;
            }
          } else {
            const newRow = {
              rowId: `row-${newRows.length}`,
              columns: [{ id: `col-${Date.now()}`, blockIds: [blockId] }],
              height: 150,
              flexWidths: [12],
            };
            newRows.splice(insertAtRowIndex, 0, newRow);
          }

          newRows.forEach((r, i) => {
            r.rowId = `row-${i}`;
          });
          return applyLayoutChange(
            currentConfig,
            { ...spec, blocks: [...spec.blocks, newBlock] },
            { rows: newRows }
          );
        });
      } else {
        const typeLabels: Record<string, string> = {
          chart: "chart",
          metric_card: "KPI metric card",
          ai_image: "AI image",
          text_block: "text",
        };
        const typeLabel = typeLabels[blockType] ?? blockType;

        // Build positional hint based on where the + button was clicked
        let positionHint = "";
        if (adjacentContext?.block) {
          const blockRef = buildBlockRefTag(adjacentContext.block.block_id);
          positionHint = `${adjacentContext.placement} ${blockRef}`;
        } else if (insertAtRowIndex === 0) {
          positionHint = "at the top of the dashboard";
        } else {
          positionHint = "at the bottom of the dashboard";
        }

        const userPrompt = prompt
          ? `Add ${typeLabel} ${positionHint}: ${prompt}`
          : `Add ${typeLabel} ${positionHint}`;
        const message = `${userPrompt}`;

        setIsChatSidebarOpen(true);

        setPendingMessages(prev => [
          ...prev,
          {
            id: `pending-add-${Date.now()}`,
            role: "user" as const,
            content: message,
            autoSubmit: true,
          },
        ]);
      }
    },
    [dashboardId, applyDashboardChanges, queryClient]
  );

  const handleExplainBlockInChat = useCallback(
    (blockId: string, errorMessage?: string, errorStateDetails?: ErrorStateDetails | null) => {
      const blockRef = buildBlockRefTag(blockId);

      // Two flows feed this callback:
      // 1. Context-menu "Explain" — no errorMessage; send a simple "Explain this block".
      // 2. Block error fallback "Learn more" — errorMessage present; embed the
      //    console error verbatim so the agent can reason about the cause.
      //    When BE-attributed, `errorStateDetails.tabs` names the source tabs
      //    implicated in the failure — surface them to the agent explicitly
      //    since the visible error_state text is intentionally generic.
      const hasError = typeof errorMessage === "string" && errorMessage.length > 0;
      const implicatedTabs = errorStateDetails?.tabs;
      const hasTabs = Array.isArray(implicatedTabs) && implicatedTabs.length > 0;
      const tabsSuffix = hasTabs
        ? `\n\nImplicated source ${implicatedTabs.length === 1 ? "tab" : "tabs"}: ` +
          `${implicatedTabs.map(t => `"${t}"`).join(", ")}.`
        : "";
      const visibleMessage = hasError
        ? `Explain why ${blockRef ?? ""} is broken`
        : `Explain ${blockRef ?? ""}`;
      const backendMessage = hasError
        ? `${blockRef ?? ""} This block failed to render with the following error:\n\n` +
          `\`\`\`\n${errorMessage}\n\`\`\`${tabsSuffix}\n\nPlease explain the cause.`
        : undefined;

      setIsChatSidebarOpen(true);

      setPendingMessages(prev => [
        ...prev,
        {
          id: `pending-explain-${Date.now()}`,
          role: "user" as const,
          content: visibleMessage,
          ...(backendMessage ? { backendContent: backendMessage } : {}),
          autoSubmit: true,
        },
      ]);
    },
    [buildBlockRefTag]
  );

  // Context-menu "Explain". If the block carries a pre-computed explanation,
  // show it directly in chat as an assistant message (instant, no AI call).
  // Otherwise fall back to the live agent flow by auto-submitting the question.
  const handleExplainInChat = useCallback(
    (blockId: string) => {
      const blockRef = buildBlockRefTag(blockId);
      const visibleUserMessage = `Explain ${blockRef ?? ""}`;

      let storedExplanation: string | undefined;
      try {
        const currentData = queryClient.getQueryData<GetWebDashboard200Response>([
          QueryCacheKey.WEB_DASHBOARD,
          dashboardId,
        ]);
        const spec = currentData?.content_config?.content_spec as
          | WebDashboardContentSpec
          | undefined;
        const block = spec?.blocks?.find(b => b.block_id === blockId);
        if (typeof block?.explanation === "string" && block.explanation.trim()) {
          storedExplanation = block.explanation;
        }
      } catch {
        // Ignore lookup errors and fall back to the live agent flow.
      }

      setIsChatSidebarOpen(true);

      if (storedExplanation) {
        const now = Date.now();
        setPendingMessages(prev => [
          ...prev,
          {
            id: `pending-explain-q-${now}`,
            role: "user" as const,
            content: visibleUserMessage,
            instant: true,
          },
          {
            id: `pending-explain-a-${now}`,
            role: "agent" as const,
            content: storedExplanation as string,
            instant: true,
          },
        ]);
        return;
      }

      setPendingMessages(prev => [
        ...prev,
        {
          id: `pending-explain-${Date.now()}`,
          role: "user" as const,
          content: visibleUserMessage,
          autoSubmit: true,
        },
      ]);
    },
    [buildBlockRefTag, dashboardId, queryClient]
  );

  const handleFixBlock = useCallback(
    (blockId: string, errorMessage: string) => {
      const isSlicer = blockId.startsWith("slicer_");

      // Defensive guard: blocks whose error_state is set by the backend are
      // BE-attributed structural failures (e.g. excluded/deleted referenced
      // tab, missing table). For those the Fix this button should not render
      // at all (see BlockErrorState / dashboard renderer). This early-return
      // is belt-and-suspenders so a stale client can never kick off an
      // auto-fix loop for a non-runtime error. Slicers don't carry an
      // error_state in v1 and continue to use the normal flow.
      if (!isSlicer) {
        const currentDashboardData = queryClient.getQueryData<GetWebDashboard200Response>([
          QueryCacheKey.WEB_DASHBOARD,
          dashboardId,
        ]);
        const contentSpec = currentDashboardData?.content_config?.content_spec as
          | WebDashboardContentSpec
          | undefined;
        const block = contentSpec?.blocks?.find(b => b.block_id === blockId);
        if (block?.error_state) {
          return;
        }
      }

      const blockRef = isSlicer ? "" : buildBlockRefTag(blockId);
      const slicerLabel = isSlicer ? blockId.replace("slicer_", "") : "";

      const visibleMessage = isSlicer
        ? `[slicer name="${escapeForBlockRef(slicerLabel)}"] Fix this filter`
        : `${blockRef ?? ""} Fix this block`;
      const backendMessage = isSlicer
        ? `The filter "${slicerLabel}" failed to render with the following error:\n\n` +
          `\`\`\`\n${errorMessage}\n\`\`\`\n\nPlease fix it.`
        : `${blockRef ?? ""} This block failed to render with the following error:\n\n` +
          `\`\`\`\n${errorMessage}\n\`\`\`\n\nPlease fix it.`;

      setIsChatSidebarOpen(true);

      setPendingMessages(prev => [
        ...prev,
        {
          id: `pending-fix-${Date.now()}`,
          role: "user" as const,
          content: visibleMessage,
          backendContent: backendMessage,
          autoSubmit: true,
          meta: {
            auto_fix: {
              is_auto_fix: true,
              source: "fix_button",
            },
          },
        },
      ]);
    },
    [buildBlockRefTag, dashboardId, queryClient]
  );

  const handleRefreshDashboard = async (
    tabsConfig?: WebDashboardTabsConfig,
    excelUploadS3Key?: string,
    spreadsheetName?: string,
    excelUploadClientMetrics?: {
      file_size_bytes: number;
      upload_type: "post" | "multipart";
      upload_duration_ms: number;
      multipart_part_count?: number;
    }
  ) => {
    if (!dashboardId || isRefreshing) {
      return;
    }

    // Mirror chat behavior: if user isn't authenticated (or auth is stale), prompt OAuth
    // and preserve the pending refresh instead of silently no-op'ing.
    if (!isAuthenticated) {
      sessionStorage.setItem(
        "web_dashboard_pending_refresh",
        JSON.stringify({
          tabs_config: tabsConfig ?? null,
          excel_upload_s3_key: excelUploadS3Key ?? null,
          spreadsheet_name: spreadsheetName ?? null,
          excel_upload_client_metrics: excelUploadClientMetrics ?? null,
        })
      );
      if (oauthProvider === "microsoft") {
        setIsMicrosoftAuthModalOpen(true);
      } else {
        setIsGoogleSheetsAuthModalOpen(true);
      }
      return;
    }
    // Disable refresh for expired trial users
    if (trialExpired) {
      return;
    }

    // Track refresh clicked
    track(AnalyticsEventNameType.VibeDashRefreshClicked, {
      vibe_dash_id: dashboardId,
      ss_id: dashboardData?.spreadsheet_id,
      vibe_dash_name: dashboardData?.content_config?.title,
      vibe_dash_slicers: slicerNames,
    });

    try {
      // Pre-flight (diff / impact preview) can be slow for large local Excel uploads.
      // Show a snackbar so the UI doesn't feel "hung" before the refresh run_id exists.
      setIsCheckingChanges(true);
      const result = await refreshDashboardMutation.mutateAsync({
        forceRefresh: false,
        tabsConfig,
        excelUploadS3Key,
        spreadsheetName,
        excelUploadClientMetrics,
      });

      // Handle different response statuses
      if ((result as any).status === "archived") {
        setToastProps({
          message: (result as any).message || "This dashboard is archived and can't be refreshed.",
          type: "warning",
          materialIcon: "inventory_2",
          duration: 5000,
          onClose: () => setToastProps(null),
        });
        return;
      }

      // E.g. a local Excel dashboard refreshed without a fresh workbook upload (the
      // conflict-abort "Try again" retry has no upload key). The message is actionable
      // ("Upload an .xlsx file…") — surface it as a toast, not the failure alert.
      if ((result as any).status === "forbidden") {
        setToastProps({
          message: (result as any).message || "This dashboard can't be refreshed right now.",
          type: "error",
          position: "bottom",
          disableAutoClose: true,
          onClose: () => setToastProps(null),
        });
        return;
      }

      if ((result as any).status === "needs_oauth") {
        sessionStorage.setItem(
          "web_dashboard_pending_refresh",
          JSON.stringify({
            tabs_config: tabsConfig ?? null,
            excel_upload_s3_key: excelUploadS3Key ?? null,
            spreadsheet_name: spreadsheetName ?? null,
            excel_upload_client_metrics: excelUploadClientMetrics ?? null,
          })
        );
        if (oauthProvider === "microsoft") {
          setIsMicrosoftAuthModalOpen(true);
        } else {
          setGoogleOAuthErrorReason((result as any).oauth_error_reason ?? "token_expired");
          setIsGoogleSheetsAuthModalOpen(true);
        }
        return;
      }

      if ((result as any).status === "needs_confirmation") {
        const ratio = Number((result as any).predicted_blocks_changed_ratio ?? 0);
        const changedCount = Number((result as any).predicted_blocks_changed_count ?? 0);
        const totalCount = Number((result as any).predicted_blocks_total_count ?? 0);
        setLargeChangeConfirmation({
          tabsConfig,
          excelUploadS3Key,
          spreadsheetName,
          excelUploadClientMetrics,
          ratio,
          changedCount,
          totalCount,
        });
        return;
      }

      if (result.status === "already_running") {
        // Use existing run_id if refresh is already running
        if (result.existing_run_id) {
          setRefreshRunId(result.existing_run_id);
          setRefreshStartTime(Date.now());
        } else {
          throw new Error("Refresh already running but no run_id provided");
        }
      } else if (result.status === "started" && result.run_id) {
        // Start polling for the new run
        setRefreshRunId(result.run_id);
        setRefreshStartTime(Date.now());

        // Track refresh started
        track(AnalyticsEventNameType.VibeDashRefreshStarted, {
          vibe_dash_id: dashboardId,
          ss_id: dashboardData?.spreadsheet_id,
          vibe_dash_name: dashboardData?.content_config?.title,
          vibe_dash_slicers: slicerNames,
        });
      } else {
        throw new Error(result.message || "Unknown refresh status");
      }
    } catch (error) {
      console.error("Error refreshing dashboard:", error);

      // Mirror chat behavior: treat 401/403 as auth-required and show the correct OAuth modal.
      if (isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 401 || status === 403) {
          sessionStorage.setItem(
            "web_dashboard_pending_refresh",
            JSON.stringify({
              tabs_config: tabsConfig ?? null,
              excel_upload_s3_key: excelUploadS3Key ?? null,
              spreadsheet_name: spreadsheetName ?? null,
              excel_upload_client_metrics: excelUploadClientMetrics ?? null,
            })
          );
          if (oauthProvider === "microsoft") {
            setIsMicrosoftAuthModalOpen(true);
          } else {
            setIsGoogleSheetsAuthModalOpen(true);
          }
          return;
        }
      }

      // Track refresh failed (API call error)
      track(AnalyticsEventNameType.VibeDashRefreshFailed, {
        vibe_dash_id: dashboardId,
        ss_id: dashboardData?.spreadsheet_id,
        vibe_dash_name: dashboardData?.content_config?.title,
        vibe_dash_slicers: slicerNames,
        error_message: error instanceof Error ? error.message : "Unknown error",
      });

      alert(
        `Failed to refresh dashboard: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    } finally {
      setIsCheckingChanges(false);
    }
  };

  // Latest handler for the run-status effect's "Try again" action (declared above it),
  // mirroring the handleUndoRef pattern.
  const handleRefreshDashboardRef = useRef(handleRefreshDashboard);

  handleRefreshDashboardRef.current = handleRefreshDashboard;

  const handleConfirmLargeChangeRefresh = useCallback(async () => {
    if (!dashboardId || !largeChangeConfirmation || isRefreshing) {
      return;
    }
    setIsConfirmingLargeChange(true);
    try {
      const result = await refreshDashboardMutation.mutateAsync({
        forceRefresh: true,
        tabsConfig: largeChangeConfirmation.tabsConfig,
        excelUploadS3Key: largeChangeConfirmation.excelUploadS3Key,
        spreadsheetName: largeChangeConfirmation.spreadsheetName,
        // Only send client upload metrics on the initial pre-flight request (forceRefresh=false).
        // The confirm retry should not double-report.
        excelUploadClientMetrics: undefined,
      });

      setLargeChangeConfirmation(null);

      if (result.status === "already_running") {
        if (result.existing_run_id) {
          setRefreshRunId(result.existing_run_id);
          setRefreshStartTime(Date.now());
          return;
        }
        throw new Error("Refresh already running but no run_id provided");
      }

      if (result.status === "started" && result.run_id) {
        setRefreshRunId(result.run_id);
        setRefreshStartTime(Date.now());
        return;
      }

      if ((result as any).status === "needs_oauth") {
        sessionStorage.setItem(
          "web_dashboard_pending_refresh",
          JSON.stringify({
            tabs_config: largeChangeConfirmation.tabsConfig ?? null,
            excel_upload_s3_key: largeChangeConfirmation.excelUploadS3Key ?? null,
            spreadsheet_name: largeChangeConfirmation.spreadsheetName ?? null,
            excel_upload_client_metrics: null,
          })
        );
        if (oauthProvider === "microsoft") {
          setIsMicrosoftAuthModalOpen(true);
        } else {
          setGoogleOAuthErrorReason((result as any).oauth_error_reason ?? "token_expired");
          setIsGoogleSheetsAuthModalOpen(true);
        }
        return;
      }

      throw new Error(result.message || "Unknown refresh status");
    } catch (e) {
      console.error("Error confirming large-change refresh:", e);
      alert(`Failed to refresh dashboard: ${e instanceof Error ? e.message : "Unknown error"}`);
    } finally {
      setIsConfirmingLargeChange(false);
    }
  }, [dashboardId, largeChangeConfirmation, isRefreshing, oauthProvider, refreshDashboardMutation]);

  const handleUndoRefresh = useCallback(async () => {
    if (!dashboardId || !undoRefreshSnapshotS3Path || isUndoingRefresh || isRefreshing) {
      return;
    }
    setIsUndoingRefresh(true);
    try {
      await DEPRECATED_api.request({
        method: "POST",
        url: `/api/web_dashboard/${dashboardId}/undo_refresh`,
        body: {
          undo_web_dashboard_json_s3_path: undoRefreshSnapshotS3Path,
        },
      });
      localStorage.removeItem(`web_dashboard_refresh_undo:${dashboardId}`);
      setUndoRefreshSnapshotS3Path(null);
      window.location.reload();
    } catch (e) {
      console.error("Undo refresh failed:", e);
      alert(`Failed to undo refresh: ${e instanceof Error ? e.message : "Unknown error"}`);
    } finally {
      setIsUndoingRefresh(false);
    }
  }, [DEPRECATED_api, dashboardId, undoRefreshSnapshotS3Path, isUndoingRefresh, isRefreshing]);

  // Auto-retry pending actions after OAuth redirect returns.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const oauthSuccess = params.get("oauth_success") === "true";
    if (!oauthSuccess) {
      return;
    }

    // Clean URL first to avoid loops if the retry triggers another navigation.
    params.delete("oauth_success");
    params.delete("oauth_cancelled");
    params.delete("oauth_error");
    const qs = params.toString();
    history.replace({
      pathname: location.pathname,
      search: qs ? `?${qs}` : "",
    });

    const pendingRefreshRaw = sessionStorage.getItem("web_dashboard_pending_refresh");
    if (pendingRefreshRaw) {
      // On page load, permissions may not have resolved yet. If we retry too early,
      // handleRefreshDashboard() will no-op and we'd lose the pending refresh.
      if (!isAuthenticated || loading) {
        setShouldRetryPendingRefreshAfterOAuth(true);
      } else {
        try {
          const parsed = JSON.parse(pendingRefreshRaw) as {
            tabs_config?: WebDashboardTabsConfig | null;
            excel_upload_s3_key?: string | null;
            spreadsheet_name?: string | null;
            excel_upload_client_metrics?: {
              file_size_bytes: number;
              upload_type: "post" | "multipart";
              upload_duration_ms: number;
              multipart_part_count?: number;
            } | null;
          };
          sessionStorage.removeItem("web_dashboard_pending_refresh");
          void handleRefreshDashboard(
            parsed.tabs_config ?? undefined,
            parsed.excel_upload_s3_key ?? undefined,
            parsed.spreadsheet_name ?? undefined,
            parsed.excel_upload_client_metrics ?? undefined
          );
        } catch {
          // Ignore malformed pending payloads
          sessionStorage.removeItem("web_dashboard_pending_refresh");
        }
      }
    }

    const pendingChatRaw = sessionStorage.getItem("web_dashboard_pending_chat_message");
    if (pendingChatRaw) {
      // Similar to refresh: wait until permissions are loaded so we can restore the
      // correct chat mode (editor vs viewer) and avoid creating a new thread type.
      if (!isAuthenticated || loading) {
        setShouldRetryPendingChatAfterOAuth(true);
      } else {
        try {
          const parsed = JSON.parse(pendingChatRaw) as {
            message: string;
            prior_messages?: Array<{ role: "user" | "agent"; content: string }> | null;
            meta?: any;
            chat_mode?: "editor" | "viewer";
            thread_id?: string | null;
          };
          sessionStorage.removeItem("web_dashboard_pending_chat_message");
          if (parsed.message) {
            if (parsed.chat_mode === "editor" && canEditDashboard) {
              setDashboardMode("editor");
            }
            if (parsed.thread_id && parsed.chat_mode) {
              try {
                const agentType =
                  parsed.chat_mode === "viewer" ? "web_dashboard_viewer" : "web_dashboard_editor";
                localStorage.setItem(
                  `web_dashboard_thread_id:${dashboardId}:${agentType}`,
                  parsed.thread_id
                );
              } catch {
                // ignore storage failures
              }
            }
            setIsChatOAuthRequired(false);
            setIsChatSidebarOpen(true);
            const prior = parsed.prior_messages ?? [];
            setPendingMessages([
              ...prior.map(pm => ({
                id: `pending-prior-${Date.now()}-${Math.random()}`,
                role: pm.role,
                content: pm.content,
              })),
              {
                id: `pending-user-${Date.now()}`,
                role: "user" as const,
                content: parsed.message,
                autoSubmit: true,
                meta: parsed.meta ?? undefined,
              },
            ]);
          }
        } catch {
          // Ignore malformed pending payloads
          sessionStorage.removeItem("web_dashboard_pending_chat_message");
        }
      }
    }
    // Intentionally only runs on oauth_success transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (!shouldRetryPendingChatAfterOAuth) {
      return;
    }
    if (!isAuthenticated || loading) {
      return;
    }

    const pendingChatRaw = sessionStorage.getItem("web_dashboard_pending_chat_message");
    if (!pendingChatRaw) {
      setShouldRetryPendingChatAfterOAuth(false);
      return;
    }

    try {
      const parsed = JSON.parse(pendingChatRaw) as {
        message: string;
        prior_messages?: Array<{ role: "user" | "agent"; content: string }> | null;
        meta?: any;
        chat_mode?: "editor" | "viewer";
        thread_id?: string | null;
      };
      sessionStorage.removeItem("web_dashboard_pending_chat_message");
      if (parsed.message) {
        if (parsed.chat_mode === "editor" && canEditDashboard) {
          setDashboardMode("editor");
        }
        if (parsed.thread_id && parsed.chat_mode) {
          try {
            const agentType =
              parsed.chat_mode === "viewer" ? "web_dashboard_viewer" : "web_dashboard_editor";
            localStorage.setItem(
              `web_dashboard_thread_id:${dashboardId}:${agentType}`,
              parsed.thread_id
            );
          } catch {
            // ignore storage failures
          }
        }
        setIsChatOAuthRequired(false);
        setIsChatSidebarOpen(true);
        const prior = parsed.prior_messages ?? [];
        setPendingMessages([
          ...prior.map(pm => ({
            id: `pending-prior-${Date.now()}-${Math.random()}`,
            role: pm.role,
            content: pm.content,
          })),
          {
            id: `pending-user-${Date.now()}`,
            role: "user" as const,
            content: parsed.message,
            autoSubmit: true,
            meta: parsed.meta ?? undefined,
          },
        ]);
      }
    } catch {
      sessionStorage.removeItem("web_dashboard_pending_chat_message");
    } finally {
      setShouldRetryPendingChatAfterOAuth(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRetryPendingChatAfterOAuth, isAuthenticated, loading, canEditDashboard]);

  // If OAuth returned but auth/permissions weren't ready yet, retry the pending refresh
  // once the page finishes loading.
  useEffect(() => {
    if (!shouldRetryPendingRefreshAfterOAuth) {
      return;
    }
    if (!isAuthenticated || loading || isRefreshing) {
      return;
    }

    const pendingRefreshRaw = sessionStorage.getItem("web_dashboard_pending_refresh");
    if (!pendingRefreshRaw) {
      setShouldRetryPendingRefreshAfterOAuth(false);
      return;
    }

    try {
      const parsed = JSON.parse(pendingRefreshRaw) as {
        tabs_config?: WebDashboardTabsConfig | null;
        excel_upload_s3_key?: string | null;
        spreadsheet_name?: string | null;
        excel_upload_client_metrics?: {
          file_size_bytes: number;
          upload_type: "post" | "multipart";
          upload_duration_ms: number;
          multipart_part_count?: number;
        } | null;
      };
      sessionStorage.removeItem("web_dashboard_pending_refresh");
      void handleRefreshDashboard(
        parsed.tabs_config ?? undefined,
        parsed.excel_upload_s3_key ?? undefined,
        parsed.spreadsheet_name ?? undefined,
        parsed.excel_upload_client_metrics ?? undefined
      );
    } catch {
      sessionStorage.removeItem("web_dashboard_pending_refresh");
    } finally {
      setShouldRetryPendingRefreshAfterOAuth(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRetryPendingRefreshAfterOAuth, isAuthenticated, loading, isRefreshing]);

  const showRefreshSuccessToast = () => {
    setToastProps({
      message: "Dashboard refreshed successfully",
      type: "success",
      materialIcon: "check_circle",
      onClose: () => {
        setToastProps(null);
      },
    });
  };

  // Handle timeout for refresh polling
  useEffect(() => {
    if (!refreshStartTime || !refreshRunId) {
      return;
    }

    const checkTimeout = () => {
      if (Date.now() - refreshStartTime > MANUAL_REFRESH_TIMEOUT_MS) {
        setRefreshRunId(null);
        setRefreshStartTime(null);
        setToastProps({
          message: "Refresh is taking longer than expected. Check your dashboard after some time.",
          type: "info",
          materialIcon: "info",
        });
      }
    };

    const timeoutInterval = setInterval(checkTimeout, 1000);
    return () => clearInterval(timeoutInterval);
  }, [refreshStartTime, refreshRunId]);

  // Check for refresh success flag on mount
  useEffect(() => {
    const refreshSuccessFlag = localStorage.getItem("dashboard_refresh_success");
    if (refreshSuccessFlag === "true") {
      // Clear the flag
      localStorage.removeItem("dashboard_refresh_success");
      // Show refresh success toast after a short delay to ensure page is loaded
      setTimeout(() => {
        showRefreshSuccessToast();
      }, 500);
    }
  }, []);

  // Handle reset slicers with analytics tracking
  // Uses trackDashboardEvent to support both authenticated and anonymous users
  const handleResetSlicers = useCallback(() => {
    trackDashboardEvent(AnalyticsEventNameType.VibeDashSlicerReset, {
      vibe_dash_id: dashboardId,
      vibe_dash_name: dashboardData?.content_config?.title,
      ss_id: dashboardData?.spreadsheet_id,
      vibe_dash_slicers: slicerNames,
      vibe_dash_viewer_type: canEditDashboard
        ? VibeDashViewerType.Owner
        : VibeDashViewerType.Viewer,
    });
  }, [trackDashboardEvent, dashboardId, dashboardData, slicerNames, canEditDashboard]);

  // Handle slicer change with analytics tracking
  // Uses trackDashboardEvent to support both authenticated and anonymous users
  const handleSlicerChange = useCallback(
    (slicerName: string, slicerType: string) => {
      trackDashboardEvent(AnalyticsEventNameType.VibeDashSlicerChanged, {
        vibe_dash_id: dashboardId,
        vibe_dash_name: dashboardData?.content_config?.title,
        ss_id: dashboardData?.spreadsheet_id,
        vibe_dash_slicers: slicerNames,
        vibe_dash_slicer_name: slicerName,
        vibe_dash_slicer_type: slicerType,
      });
    },
    [trackDashboardEvent, dashboardId, dashboardData, slicerNames]
  );

  // Persist the current filter state as what this dashboard opens on. Uses the same path as
  // every other manual spec edit, so it lands in the undo stack, updates the cache
  // optimistically, and rolls back (with its own toast) on failure.
  const handleSaveSlicerDefaults = useCallback(
    async (snapshot: FilterDefaultsSnapshot) => {
      // applyDashboardChanges resolves even when the save fails (it rolls back and toasts
      // instead of rejecting), and early-returns without saving when content_spec is missing.
      // SlicersSection expects a rejection on failure, so only a confirmed save resolves.
      let saved = false;
      await applyDashboardChanges(
        (currentConfig, contentSpec) => ({
          ...currentConfig,
          content_spec: {
            ...contentSpec,
            slicers: withSavedDefaults(contentSpec.slicers ?? [], snapshot),
          },
        }),
        {
          onSuccess: () => {
            saved = true;
          },
        }
      );
      if (!saved) {
        throw new Error("Failed to save slicer defaults");
      }
      trackDashboardEvent(AnalyticsEventNameType.VibeDashFilterDefaultsSaved, {
        vibe_dash_id: dashboardId,
        vibe_dash_name: dashboardData?.content_config?.title,
        ss_id: dashboardData?.spreadsheet_id,
        vibe_dash_slicers: slicerNames,
      });
    },
    [applyDashboardChanges, trackDashboardEvent, dashboardId, dashboardData, slicerNames]
  );

  // Handle Command+Z / Ctrl+Z for undo
  useEffect(() => {
    const handleUndoKeyboard = (event: KeyboardEvent) => {
      // Check for Cmd+Z (Mac) or Ctrl+Z (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === "z" && !event.shiftKey) {
        // Don't intercept if user is typing in an input or textarea
        const target = event.target as HTMLElement;
        if (
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable
        ) {
          return;
        }

        // Prevent undo if there's nothing to undo or if a save operation is in progress
        if (undoStack.length > 0 && !saveDashboardConfigMutation.isLoading) {
          event.preventDefault();
          handleUndo({ showToast: false });
        }
      }
    };

    // Listen on main document
    document.addEventListener("keydown", handleUndoKeyboard);

    // Also listen on iframe document if available
    const iframeDocument = iframeWindow?.document;
    if (iframeDocument) {
      iframeDocument.addEventListener("keydown", handleUndoKeyboard);
    }

    return () => {
      document.removeEventListener("keydown", handleUndoKeyboard);
      if (iframeDocument) {
        iframeDocument.removeEventListener("keydown", handleUndoKeyboard);
      }
    };
  }, [iframeWindow, undoStack.length, handleUndo, saveDashboardConfigMutation.isLoading]);

  if (!iframeBody || !iframeWindow) {
    return (
      <iframe
        ref={iframeRef}
        id="dashboardContentIframe"
        srcDoc={iframeSrcDoc}
        onLoad={handleIframeLoad}
        className="fixed inset-0 w-full h-full border-none z-0"
        style={{ display: "block", border: "none", margin: 0, padding: 0 }}
        frameBorder="0"
        title="Dashboard Frame"
      />
    );
  }

  const bottomDrawer = (
    <>
      <div
        onClick={handleBottomDrawerBackdropClick}
        className={
          "fixed inset-0 bg-black/70 z-200 flex items-center justify-center transition-opacity duration-300 ease-in-out " +
          (bottomDrawerContent ? "opacity-100" : "opacity-0 pointer-events-none")
        }
      />
      {/* The flex + items-end layout makes it so that the bottom drawer takes the height of its contents */}
      <div
        id="bottomDrawer"
        ref={bottomDrawerRef}
        className={
          "fixed flex items-end z-200 left-0 bottom-0 rounded-t-xl shadow-2xl shadow-black-600 p-4 transform transition-transform duration-300 ease-in-out bg-main-background text-main-foreground " +
          // When closed, translate-y-full alone only pushes it down by its own height, leaving it
          // parked right at the fold — iOS/desktop Safari's dynamic URL bar and rubber-band
          // overscroll then reveal this fixed bottom bar. `invisible` makes it non-paintable so no
          // viewport quirk can surface it; it stays mounted and slides up (becomes visible) on open.
          (bottomDrawerContent ? "translate-y-0" : "translate-y-full invisible pointer-events-none")
        }
        style={{ right: isChatSidebarOpen ? effectiveSidebarWidth : 0 }}
      >
        {/* X button */}
        <button
          onClick={() => setBottomDrawerContent(null)}
          className="absolute top-4 right-4 transition-colors opacity-60 hover:opacity-100 cursor-pointer"
          aria-label="Close"
        >
          <span className="material-icons">close</span>
        </button>
        {/* mx-8 ensures that the bottom drawer content's vertical scrollbar, if visible, does not overlap with the X button.
            On mobile the drawer is tight to the viewport, so drop the side margin and inner padding — the drawer's own
            p-4 provides the ~16px inset (per Figma), letting the AG-Grid table span nearly full width. */}
        <div className="w-full mx-8 max-sm:mx-0">
          {/* max-h-[85vh] ensures that the bottom drawer does not extend beyond the top of the viewport.
              overscroll-y-none keeps iOS's top-edge scroll bounce from competing with the
              swipe-down-to-close drag when this scroller is at scrollTop 0. */}
          <div
            className="max-w-9xl mx-auto p-6 max-sm:p-0 w-full max-h-[85vh] overflow-y-auto overscroll-y-none"
            ref={bottomDrawerContentContainerRef}
          ></div>
        </div>
      </div>
    </>
  );

  const content = (
    <>
      <div className="min-h-screen">
        {loading ? (
          <div className="flex justify-center items-center h-screen">
            <div className="loading-spinner w-10 h-10 rounded-full border-[3px] border-gray-100 border-t-[3px] border-t-primary-blue"></div>
          </div>
        ) : error ? (
          // Error states (access denied, not found, generic) are rendered by
          // WebDashboardAccessGate at the route level, before this component mounts.
          <></>
        ) : (
          <>
            {isDashboardCreateMode && <DashboardBuildLoaderStyles />}
            {shouldShowDashboardBuildConfetti && <DashboardBuildCompletionConfetti />}
            {showFirstDashboardAttributionIframe && userEmail && (
              <EventAttributionIframe
                email={userEmail}
                event_name="first_dashboard_created"
                event_props={{
                  source_type: dashboardData?.source_type,
                  num_blocks: dashboardContentSpec?.blocks?.length,
                }}
              />
            )}
            <WebDashboardNavBar
              dashboardId={dashboardId}
              setToastProps={setToastProps}
              handleRefreshDashboard={handleRefreshDashboard}
              isRefreshing={isRefreshing}
              isCheckingChanges={isCheckingChanges}
              onDeleteBlocks={handleDeleteBlocksForTabDisable}
              themeId={themeId}
              mode={themeMode}
              background={themeBackground}
              handleThemeChange={handleThemeChange}
              targetDocument={iframeWindow.document}
              handleSignOut={handleSignOut}
              handleEditButtonClick={handleEditButtonClick}
              spreadsheetId={dashboardData?.spreadsheet_id}
              isThemeSelectorOpen={isThemeSelectorOpen}
              setIsThemeSelectorOpen={setIsThemeSelectorOpen}
              onPreviewTheme={previewTheme}
              onThemeCommitted={handleThemeCommitted}
              onThemeUncommitted={handleThemeUncommitted}
              onThemeDeleted={handleThemeDeleted}
              onThemeDeleteFailed={handleThemeDeleteFailed}
              onRequestTheme={handleRequestTheme}
              onEditTheme={isCustomThemeAgentEnabled ? handleEditTheme : undefined}
              isChatSidebarOpen={isEffectiveChatSidebarOpen}
              chatSidebarWidth={sidebarWidth}
              isPublicDashboard={isPublicDashboard}
              isArchived={isArchivedDashboard}
              hasSuperAdminAccess={hasSuperAdminAccess}
              superAdminExpirationDt={superAdminExpirationDt}
              requestSuperAdminView={requestSuperAdminView}
              isDashboardBuilding={isDashboardCreateMode && !isDashboardBuildFailed}
              ownerWebDashboardTrial={ownerWebDashboardTrial}
              onProactiveUpgradeClick={() =>
                openUpgrade("proactive", () => setIsProactiveUpgradeModalOpen(true))
              }
              hasPersonalizedViews={hasPersonalizedViews}
              onOpenPersonalizedViews={(message: string) => {
                setIsChatSidebarOpen(true);
                prefillComposer(message);
              }}
              name={userName}
            />

            {isChatUnavailableModalOpen && (
              <ChatUnavailableModal onClose={() => setIsChatUnavailableModalOpen(false)} />
            )}
            {isUpgradeModalOpen && (
              <WebDashboardUpgradeModal
                dashboardId={dashboardId}
                onClose={() => setIsUpgradeModalOpen(false)}
              />
            )}
            {isProactiveUpgradeModalOpen && (
              <WebDashboardUpgradeModal
                variant="proactive"
                dashboardId={dashboardId}
                onClose={() => setIsProactiveUpgradeModalOpen(false)}
              />
            )}
            {isTrialInfoModalOpen && (
              <WebDashboardUpgradeModal
                variant="trial_info"
                dashboardId={dashboardId}
                onClose={() => setIsTrialInfoModalOpen(false)}
              />
            )}

            <Modal
              open={Boolean(largeChangeConfirmation)}
              centered
              maskClosable={!isConfirmingLargeChange}
              closable={!isConfirmingLargeChange}
              closeIcon={<CloseOutlined />}
              onCancel={() => {
                if (isConfirmingLargeChange) {
                  return;
                }
                setLargeChangeConfirmation(null);
              }}
              bodyStyle={{ padding: 24 }}
              footer={
                <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                  <Button
                    type="link"
                    disabled={isConfirmingLargeChange}
                    onClick={() => {
                      if (isConfirmingLargeChange) {
                        return;
                      }
                      setLargeChangeConfirmation(null);
                    }}
                    style={{ padding: 0 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={isConfirmingLargeChange}
                    loading={isConfirmingLargeChange}
                    onClick={handleConfirmLargeChangeRefresh}
                    style={{
                      background: "#EEF4FF",
                      borderColor: "#EEF4FF",
                      color: "#3053FF",
                      fontWeight: 600,
                      borderRadius: 8,
                    }}
                  >
                    Proceed with Refresh
                  </Button>
                </div>
              }
            >
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <ExclamationCircleFilled style={{ color: "#f5a623", fontSize: 20, marginTop: 2 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: "#111827" }}>
                    Data Structure Changed
                  </div>
                  <div style={{ marginTop: 8, fontSize: 14, lineHeight: "20px", color: "#4B5563" }}>
                    The new spreadsheet is structured differently from the previous version, so some
                    charts may no longer work correctly.
                  </div>
                </div>
              </div>
            </Modal>

            {undoRefreshSnapshotS3Path && (
              <div style={{ padding: "12px 24px 0" }}>
                <div
                  style={{
                    borderRadius: 8,
                    border: "1px solid #E5E7EB",
                    background: "#FFFFFF",
                    padding: "8px 12px",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <CheckCircleFilled style={{ color: "#16A34A", fontSize: 18 }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                      Dashboard refreshed
                    </span>
                    <span
                      style={{
                        fontSize: 13,
                        color: "#4B5563",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Some charts may need attention due to spreadsheet changes
                    </span>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <Button
                      type="link"
                      disabled={isUndoingRefresh || isRefreshing}
                      onClick={() => void handleUndoRefresh()}
                      style={{
                        padding: 0,
                        fontSize: 13,
                        fontWeight: 500,
                        color: isUndoingRefresh || isRefreshing ? "#9CA3AF" : "#3053FF",
                      }}
                    >
                      {isUndoingRefresh ? "Restoring..." : "Undo refresh"}
                    </Button>
                    <Button
                      type="text"
                      aria-label="Close"
                      icon={<CloseOutlined />}
                      onClick={() => {
                        localStorage.removeItem(`web_dashboard_refresh_undo:${dashboardId}`);
                        setUndoRefreshSnapshotS3Path(null);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Historical dashboard version banner (super admin only) */}
            {superAdminDashboardVersions.versionS3Url &&
              superAdminDashboardVersions.versionS3Timestamp && (
                <div className="bg-amber-100 border-b border-amber-300 px-4 py-2 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-amber-800">
                    <span className="material-icons !text-lg">history</span>
                    <span className="text-sm font-medium">
                      Viewing historical version from{" "}
                      {new Date(superAdminDashboardVersions.versionS3Timestamp).toLocaleString()}
                    </span>
                  </div>
                  <button
                    onClick={superAdminDashboardVersions.resetToCurrentVersion}
                    className="px-3 py-1 text-sm bg-amber-200 hover:bg-amber-300 text-amber-900 rounded transition-colors"
                  >
                    Back to current
                  </button>
                </div>
              )}

            <div className="flex flex-row justify-between">
              {/* Counter-spacer for chat sidebar, for keeping the middle content container centered
              when the chat sidebar is closed */}
              <div className="w-0"></div>

              {/* Dashboard header and main content container */}
              <div
                id="dashboardMainContainer"
                className={
                  // Make the central dashboard container take up all available horizontal space
                  // between the left and right spacers, independent of its contents (image vs.
                  // placeholder). Header and main content still use max-w-9xl mx-auto inside for
                  // responsive centering.
                  "relative flex-1 min-w-0 bg-dashboard-container rounded-dashboard-container shadow-dashboard-container border border-dashboard-container " +
                  // Extra margin-bottom is necessary to allow the user to drag the bottom-most row of
                  // blocks down to expand it
                  (dashboardMode === "editor" && !isDashboardCreateMode ? "mb-[100px]" : "mb-10")
                }
              >
                {trialExpired && !shouldShowDashboardBuildLoader && (
                  <div className="max-w-9xl mx-auto px-6 pt-1 max-sm:px-[12px] max-sm:pt-[8px]">
                    <div className="flex items-center justify-between rounded-lg bg-[#fdf2dc] px-4 py-2.5">
                      <div className="flex items-center gap-2 text-[13px] text-[#111215]">
                        <span className="material-icons !text-[16px] text-[#f5a623]">error</span>
                        <strong className="font-semibold">
                          {canEditDashboard
                            ? "Dashboard trial expired"
                            : "This dashboard is no longer refreshing"}
                        </strong>
                        <span>
                          {canEditDashboard
                            ? "Upgrade your plan to continue creating, editing and refreshing dashboards."
                            : ownerEmail
                            ? `Contact the owner (${ownerEmail}) to upgrade`
                            : "Contact the owner to upgrade"}
                        </span>
                      </div>
                      {canEditDashboard && (
                        <button
                          onClick={() => openUpgrade("expired", () => setIsUpgradeModalOpen(true))}
                          className="ml-4 whitespace-nowrap rounded-md bg-[#3053ff] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#2742cc]"
                          style={{ cursor: "pointer", border: "none" }}
                        >
                          Upgrade
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {showTrialStartBanner && !isDashboardCreateMode && (
                  <WebDashboardTrialStartBanner
                    dismissKey={permissions?.user_id ?? ""}
                    onLearnMore={() => setIsTrialInfoModalOpen(true)}
                    showLearnMore={isPaidUser}
                  />
                )}

                {/* max-sm on #mainHeader / #mainContent below: the mobile Figma insets dashboard
                    content 12px from the left/right and 8px from the top/bottom of the frame, where
                    `px-6 my-7` gives 21px/24.5px (the iframe root is 14px, so rem utilities land at
                    87.5%). Exact px rather than the rem scale so the inset matches the design at
                    that root size. The header's and main's 8px vertical margins collapse into a
                    single 8px gap between them, and the frame's border stops the header's top
                    margin collapsing out through the top of the frame. */}
                {!shouldShowDashboardBuildLoader && (
                  <header
                    id="mainHeader"
                    className="max-w-9xl mx-auto px-6 my-7 max-sm:px-[12px] max-sm:my-[8px]"
                  >
                    {/* Explicit px sizes: the iframe root pins font-size to 14px, so rem-based
                        text utilities render at 87.5% of nominal. */}
                    {/* The pencil sits inline after the heading and only appears on hover of
                        the header (or via keyboard focus), per the Figma annotation — the same
                        reveal-on-hover treatment the block cards use for their edit affordance. */}
                    <div className="group/header flex items-center gap-[8px]">
                      <h1
                        id="dashboardTitle"
                        ref={dashboardTitleRef}
                        className="text-[40px] leading-tight font-semibold text-main-foreground mb-1"
                      >
                        {dashboardData?.content_config?.title || "Dashboard"}
                      </h1>
                      {isEditingEnabled && (
                        // Anchors the popover: usePopoverPosition positions against
                        // offsetParent, so the popover must live inside a positioned wrapper.
                        <div className="relative shrink-0 mb-1">
                          <button
                            onClick={() => setIsHeaderEditOpen(open => !open)}
                            aria-label="Edit dashboard title and description"
                            aria-expanded={isHeaderEditOpen}
                            className={`flex items-center justify-center cursor-pointer text-main-muted-foreground hover:text-main-foreground transition-[opacity,color] focus-visible:opacity-100 ${
                              isHeaderEditOpen
                                ? "opacity-100"
                                : "opacity-0 group-hover/header:opacity-100"
                            }`}
                          >
                            <PencilIcon width={16} height={16} />
                          </button>
                          {isHeaderEditOpen && (
                            <DashboardHeaderEditPopover
                              title={dashboardData?.content_config?.title || ""}
                              description={dashboardData?.content_config?.description || ""}
                              onSave={handleEditDashboardHeader}
                              onEditInChat={() => {
                                setIsHeaderEditOpen(false);
                                openChatSidebar();
                              }}
                              onClose={() => setIsHeaderEditOpen(false)}
                              targetWindow={iframeWindow}
                            />
                          )}
                        </div>
                      )}
                    </div>
                    <p id="dashboardDescription" className="text-[16px] text-main-foreground">
                      {dashboardData?.content_config?.description || ""}
                    </p>
                  </header>
                )}

                <main
                  id="mainContent"
                  ref={mainContentRef}
                  className={
                    shouldShowDashboardBuildLoader
                      ? "h-[calc(100vh-64px)] min-h-[620px]"
                      : "max-w-9xl mx-auto px-6 my-7 max-sm:px-[12px] max-sm:my-[8px]"
                  }
                >
                  {shouldShowDashboardBuildFailed ? (
                    <div className="flex flex-col justify-center items-center py-32 gap-3 text-center">
                      <span className="material-icons text-4xl text-gray-400">error_outline</span>
                      <p className="text-gray-600 font-medium">
                        This dashboard build didn’t finish.
                      </p>
                      <p className="text-main-muted-foreground max-w-md">
                        Something went wrong while building it. You can start a new build by sending
                        a message in the chat, or try again from your spreadsheet.
                      </p>
                    </div>
                  ) : shouldShowDashboardBuildLoader ? (
                    <div className="dashboard-build-embedded h-full">
                      <DashboardBuildContentLoader phase={dashboardBuildPhase} className="h-full" />
                    </div>
                  ) : dashboardContentSpec && sqliteError && !isDashboardCreateMode ? (
                    <div className="flex flex-col justify-center items-center py-32 gap-3">
                      <span className="material-icons text-4xl text-gray-400">storage</span>
                      <p className="text-gray-600 font-medium">
                        {sqliteError instanceof SQLiteS3FetchError && sqliteError.status === 404
                          ? "Database not initialized. Please refresh the dashboard to initialize it."
                          : "Failed to load dashboard data. Please try refreshing the page."}
                      </p>
                    </div>
                  ) : dashboardContentSpec ? (
                    <>
                      <WebDashboardContent
                        key={dashboardRuntimeRemountKey}
                        contentSpec={dashboardContentSpec}
                        runSQL={runSQL}
                        targetWindow={iframeWindow}
                        initialLayout={dashboardContentSpec.layout}
                        onLayoutChange={handleLayoutChange}
                        onBlockDelete={handleBlockDelete}
                        canEdit={isEditingEnabled}
                        isAiEditing={isAiThinking}
                        onExplainBlockInChat={
                          isAuthenticated ? handleExplainBlockInChat : undefined
                        }
                        onExplainInChat={isAuthenticated ? handleExplainInChat : undefined}
                        onEditBlock={isEditingEnabled ? handleEditBlock : undefined}
                        onApplyBlockEditLocally={
                          isEditingEnabled ? handleApplyBlockEditLocally : undefined
                        }
                        onCommitBlockEdits={isEditingEnabled ? handleCommitBlockEdits : undefined}
                        onAddBlock={isEditingEnabled ? handleAddBlock : undefined}
                        onResetSlicers={handleResetSlicers}
                        onSlicerChange={handleSlicerChange}
                        areAiImagesStale={areAiImagesStale}
                        refreshingBlockIds={refreshingBlockIds}
                        onBlockRendered={handleBlockRendered}
                        isSQLiteReady={isSQLiteInitializedFromHook}
                        onFixBlock={isEditingEnabled ? handleFixBlock : undefined}
                        isBuilding={isDashboardCreateMode}
                        userEmail={userEmail}
                        dashboardId={dashboardId}
                        onTableOverridesChange={
                          isEditingEnabled ? handleTableOverridesChange : undefined
                        }
                        onSaveSlicerDefaults={
                          isEditingEnabled ? handleSaveSlicerDefaults : undefined
                        }
                      />
                    </>
                  ) : (
                    <p className="text-main-muted-foreground">Loading...</p>
                  )}

                  {/* Bottom drawer for modal drill-downs data tables, etc. */}
                  {bottomDrawer}
                </main>

                {isDashboardCreateMode && !isDashboardBuildFailed && (
                  <DashboardBuildPhaseProgress phase={dashboardBuildProgressPhase} />
                )}
              </div>

              {/* Spacer for right sidebars. Takes up space when a sidebar is open so
              none of the dashboard content gets obscured. */}
              <ChatSidebarFlexSpacer
                isChatSidebarOpen={isEffectiveChatSidebarOpen || shouldShowBuilderChatSidebar}
                width={sidebarWidth}
                isResizing={isSidebarResizing}
                isPopoverBoundary
              />
            </div>

            {/* Sidebar navigation (disabled for now). Turn on by adding "{screen_size}:block" to
            className for the appropriate screen size. */}
            <aside
              className={
                "hidden fixed left-0 top-40 h-screen px-8 overflow-y-auto text-xs transition-opacity duration-300 ease-in-out " +
                (isEffectiveChatSidebarOpen
                  ? "opacity-0 pointer-events-none"
                  : "opacity-100 pointer-events-auto")
              }
            >
              <nav className="space-y-1">
                {sidebarNavItems.map(item => (
                  <a
                    key={item.id}
                    href="#"
                    onClick={e => {
                      e.preventDefault();
                      item.element.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="block py-2 px-4 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
            </aside>

            {isDashboardCreateMode && dashboardData ? (
              <WebDashboardChatSidebar
                ref={chatSidebarRef}
                webDashboardId={dashboardId || ""}
                spreadsheetId={dashboardData.spreadsheet_id || ""}
                sourceType={(dashboardData as any)?.source_type ?? null}
                isOpen={true}
                onClose={() => setIsChatSidebarOpen(true)}
                targetDocument={iframeWindow.document}
                targetWindow={iframeWindow}
                blockContexts={[]}
                onThinkingStateChange={setIsAiThinking}
                onRefreshingBlockIdsChange={setAgentInferredRefreshingBlockIds}
                pendingMessages={pendingMessages}
                addPendingMessage={msg => setPendingMessages(prev => [...prev, msg])}
                clearPendingMessages={() => setPendingMessages([])}
                mode="builder"
                initialThreadId={dashboardData.agent_thread_id ?? null}
                width={sidebarWidth}
                onWidthChange={setSidebarWidth}
                isResizing={isSidebarResizing}
                onResizingChange={setIsSidebarResizing}
              />
            ) : dashboardId && dashboardData ? (
              <WebDashboardChatSidebar
                ref={chatSidebarRef}
                webDashboardId={dashboardId}
                spreadsheetId={dashboardData.spreadsheet_id || ""}
                isArchived={isArchivedDashboard}
                sourceType={(dashboardData as any)?.source_type ?? null}
                isOpen={isChatSidebarOpen}
                onClose={() => {
                  setIsChatSidebarOpen(false);
                  setPendingMessages([]);
                }}
                onDashboardUpdate={handleSidebarDashboardUpdate}
                onBlockScreenshotCaptureRequest={handleBlockScreenshotCaptureRequest}
                targetDocument={iframeWindow.document}
                targetWindow={iframeWindow}
                blockContexts={chatBlockContexts}
                onRemoveBlockContext={blockId =>
                  startViewTransition(() => {
                    setChatBlockContexts(prev => prev.filter(ctx => ctx.blockId !== blockId));
                  })
                }
                onThinkingStateChange={setIsAiThinking}
                onRefreshingBlockIdsChange={setAgentInferredRefreshingBlockIds}
                initialSummary={planOverview}
                pendingMessages={pendingMessages}
                addPendingMessage={msg => setPendingMessages(prev => [...prev, msg])}
                clearPendingMessages={() => setPendingMessages([])}
                prefillInput={chatPrefillMessage}
                prefillHiddenContext={chatPrefillContext}
                onPrefillConsumed={handlePrefillConsumed}
                autoSubmitMessage={pendingThemeChatMessage}
                onAutoSubmitConsumed={handleAutoSubmitConsumed}
                isThemeRequestOpen={isThemeRequestOpen}
                themeRequestInstanceId={themeRequestInstanceId}
                themeRequestFocusNonce={themeRequestFocusNonce}
                onThemeRequestSubmitted={handleThemeRequestSubmitted}
                onThemeFormRequested={handleThemeFormRequested}
                onThemeRequestDismissed={handleThemeRequestDismissed}
                onThemeCardPreview={handleChatThemePreview}
                onThemeCardApply={handleChatThemeApply}
                onThemeCardCancelPreview={handleChatThemeCancelPreview}
                mode={
                  hasSuperAdminAccess ? "superAdminHistory" : isEditingEnabled ? "editor" : "viewer"
                }
                onOpenThemes={() => setIsThemeSelectorOpen(true)}
                onRevertToMessage={handleRevertToMessage}
                messageUndoEntryIds={messageToUndoEntry}
                undoStack={undoStack}
                width={sidebarWidth}
                onWidthChange={setSidebarWidth}
                isResizing={isSidebarResizing}
                onResizingChange={setIsSidebarResizing}
                onAutoFixExhausted={handleAutoFixExhausted}
                superAdminHistory={
                  hasSuperAdminAccess
                    ? {
                        onLoadVersion: superAdminDashboardVersions.loadVersion,
                        isLoadingVersion: superAdminDashboardVersions.isLoadingVersion,
                      }
                    : undefined
                }
                availableBlocks={availableBlocks}
                onAddBlockContext={handleAddBlockContext}
                onBlockContextsChange={handleBlockContextsOnSubmit}
                oauthRequiredProvider={isChatOAuthRequired ? oauthProvider : null}
                onConnectOAuth={() => {
                  if (oauthProvider === "microsoft") {
                    setIsMicrosoftAuthModalOpen(true);
                  } else {
                    setIsGoogleSheetsAuthModalOpen(true);
                  }
                }}
                onDismissOAuthRequired={() => {
                  setIsChatOAuthRequired(false);
                  sessionStorage.removeItem("web_dashboard_pending_chat_message");
                }}
                onNeedsOAuth={handleChatNeedsOAuth}
              />
            ) : null}

            {/* Toast Container */}
            {toastProps && (
              // Center the toast horizontally, position based on toast type
              <div
                className={`fixed left-1/2 -translate-x-1/2 z-[1000] ${
                  toastProps.position === "bottom" ? "bottom-6" : "top-20"
                }`}
              >
                {/* contentKey = the state object: changes exactly once per setToastProps, so a
                    replacement toast restarts its countdown even with identical content. */}
                <Toast
                  {...toastProps}
                  contentKey={toastProps}
                  onClose={() => setToastProps(null)}
                />
              </div>
            )}

            {/* Refresh pre-flight (diff / impact preview) snackbar */}
            {isCheckingChanges && !isRefreshing && (
              <CheckingChangesSnackbar
                liftForBottomToast={toastProps?.position === "bottom"}
                sidebarOffset={
                  isEffectiveChatSidebarOpen || shouldShowBuilderChatSidebar
                    ? effectiveSidebarWidth
                    : 0
                }
              />
            )}

            {/* Manual refresh progress snackbar */}
            {isRefreshing && (
              <RefreshSnackbar
                liftForBottomToast={toastProps?.position === "bottom"}
                startedMs={refreshStartTime}
                sidebarOffset={
                  isEffectiveChatSidebarOpen || shouldShowBuilderChatSidebar
                    ? effectiveSidebarWidth
                    : 0
                }
              />
            )}

            <MicrosoftAuthModal
              open={isMicrosoftAuthModalOpen}
              onCancel={() => {
                setIsMicrosoftAuthModalOpen(false);
                sessionStorage.removeItem("web_dashboard_pending_refresh");
                // If the user closes the modal, keep chat OAuth-required state so we can
                // show the reconnect prompt (and preserve the draft message).
                // Do NOT clear pending chat message; it enables later auto-submit after OAuth.
              }}
              onSuccess={() => {
                setIsMicrosoftAuthModalOpen(false);
                setIsChatOAuthRequired(false);
                setShouldRetryPendingRefreshAfterOAuth(true);
                setShouldRetryPendingChatAfterOAuth(true);
              }}
            />

            <GoogleAuthModal
              mode="sheets"
              webDashboardId={dashboardId}
              redirectPath={oauthRedirectPath}
              open={isGoogleSheetsAuthModalOpen}
              reason={googleOAuthErrorReason}
              onCancel={() => {
                setIsGoogleSheetsAuthModalOpen(false);
                setGoogleOAuthErrorReason("token_expired");
                sessionStorage.removeItem("web_dashboard_pending_refresh");
                // If the user closes the modal, keep chat OAuth-required state so we can
                // show the reconnect prompt (and preserve the draft message).
                // Do NOT clear pending chat message; it enables later auto-submit after OAuth.
              }}
            />
          </>
        )}
      </div>
    </>
  );

  return (
    <>
      <iframe
        ref={iframeRef}
        srcDoc={iframeSrcDoc}
        onLoad={handleIframeLoad}
        style={{
          display: "block",
          border: "none",
          margin: 0,
          padding: 0,
          width: "100%",
          height: "100%",
        }}
        frameBorder="0"
        title="Dashboard Frame"
      />
      {createPortal(
        <ThemeProvider initialThemeId={themeId} mode={themeMode} background={themeBackground}>
          <WebDashboardViewerContext.Provider
            value={{
              dashboardMode,
              setDashboardMode,
              dashboardId: dashboardId || "",
              dashboardName: dashboardData?.content_config?.title,
              spreadsheetId: dashboardData?.spreadsheet_id || "",
              slicerNames,
              isAiThinking,
              setIsAiThinking,
              isArchived: isArchivedDashboard,
            }}
          >
            {content}
          </WebDashboardViewerContext.Provider>
        </ThemeProvider>,
        iframeBody
      )}
    </>
  );
};
